import { atan2, cos, hash32, hypot, pow, sin } from '@gardener/core';
import { TICK_RATE, TILE_SIZE, ticks } from './config';
import { CrateKind } from './economy';
import { Ground, groundAt, isSolid, type MapDef, PropKind, setLava, yardRows } from './map';
import { BASE } from './minions';
import {
  BERSERKER,
  BUZZSAW,
  CORN_RAIL,
  DUBSTEP,
  FIREFLY,
  FIREWORK,
  FROSTBITE,
  HIVE_BEE,
  LONGSTEM,
  MORTAR_SHELL,
  POISON_DART,
  PRUNER,
  ROOT_ROCKET,
  SHIELD,
  SHOTGUN,
  SHURIKEN,
  SLUDGE,
  SPRINKLER_SPRAY,
  SUN_RAY,
  THORNFIRE,
  WEAPONS,
  WEED_BURNER,
  type WeaponDef,
  WHACKER,
} from './weapons';

/**
 * Zombie Nights (ADR-0098): a co-op survival mode. A run is day 1, night 1, day 2, night 2, … until the Greenhouse falls
 * (it opens on a day to build, its purse a Sprinkler and three fences: ADR-0168 amended, ADR-0171). By day
 * the party builds (the shop is open, `ZOMBIE_SHOP`); by night the zombies (`ZOMBIES`) rise from the night's gates and
 * march on the Greenhouse. Every number of the mode lives here.
 */

// ─── The night ────────────────────────────────────────────────────────────────────────────────────────

/**
 * The day and night cycle, the night's size and how the party's size scales it.
 * - A run opens on day 1 (ADR-0168 amended: a day to build before the first dead come); every day lasts `dayTicks`;
 *   everyone READY cuts it short.
 * - Night `n`'s zombies rise over `spawnTicks(n)` in `waveCount(n)` waves — rushes of groups at its gates, each wave's
 *   within `waveTicks`; a wave beaten before the next is due brings the next at once. A night has no clock: dawn comes
 *   once every zombie of it is dead and none is left to come, so a night is as long as its fight. A run moves fast —
 *   short days, nights that come in rushes — and a defence that holds on average leaks at a wave's peak before it falls.
 * - The nights are the same every run (ADR-0168): night `n`'s threat budget (`nightBudget(n, players)`, in
 *   `ZombieDef.cost` units), its bosses and its gates are the run's script (`RUN`), never drawn by a run's seed. The
 *   script's last night (`FINALE`, the Yoke's) ends the first act; four more acts of twenty (`LONG`, ADR-0179) take the
 *   run to night `LONG.last`, each built the same way (`ACT_NIGHTS`) round its own rushes (`ACTS`) and ending on a mega
 *   boss; past it the run goes on, endless: the budget by `endlessGrowth` a night, the dead's health by `endlessHp`.
 * - Up to the finale the dead never toughen: a zombie has its def's health every night (the party's damage grows,
 *   `defenceScale`), so a night is harder by its numbers and by what is new in it, never by a wall of health. In the
 *   long acts they toughen gently (`LONG.hp` a night), so how well a party plays shows as many nights, not a wall.
 * - A party of `p` meets `partyCount(p)` times the zombies, each but the crowd (`ZombieDef.fodder`) with `partyHp(p)` times
 *   the health: more hands answer a night's questions faster, so its questions stand longer; its crowd only grows.
 */
/** Zombie Nights' first map (Moonlit Farm): the mode's own (`MODES.zombies.map`), a run's that names none. */
export const ZOMBIE_MAP_ID = 'zombie-farm';

/**
 * Zombie Nights' maps (ADR-0176), in PLAY's order, each with its own boards: Willow Bend (one road, the one PLAY picks
 * first), Twin Hollows (two fronts), Moonlit Farm (tonight's side turns round it: the expert's). Players know them as
 * Long Way Round, Back to Back and Eight Feet Under (their `MapSource.name`, ADR-0176 amended); code and docs keep the
 * old names, as their ids do.
 */
export const ZOMBIE_MAPS: readonly string[] = ['willow-bend', 'twin-hollows', ZOMBIE_MAP_ID];

/** Is `id` one of Zombie Nights' maps? */
export const isZombieMap = (id: string): boolean => ZOMBIE_MAPS.includes(id);

/**
 * Today's map (ADR-0176 amended): one map a UTC day (`dayOf`), drawn at random among the maps after PLAY's first (the
 * gentle one is everyone's anyway), pays `SEEDS.todaysMap` once a day (`todaysMapReward` in `claimed`) to whoever is in a
 * run on it as it survives its `nights`th night since its first (`todaysMapPays`), the day being the run's first. PLAY
 * points at it only for players whose best run reached that far: a newcomer stays on the gentle map. `draw`: the draw's
 * own salt, so it follows no other daily draw.
 */
export const TODAYS_MAP = { nights: 5, draw: 0x6d61_7073 } as const;

/** Day `day`'s map (a UTC day number): a draw from the day alone, so the server and every client draw the same. */
export const todaysMap = (day: number): string => {
  const maps = ZOMBIE_MAPS.slice(1);
  return maps[(hash32(day, TODAYS_MAP.draw) >>> 0) % maps.length] as string;
};

/** Day `day`'s payment of today's map, as `claimed` keeps it (one a day, as a weekly bounty's once a week). */
export const todaysMapReward = (day: number): string => `map:${day}`;

/** Does a run on `map` from night `from`, `nights` survived (the night it reached), pay day `day`'s map? */
export const todaysMapPays = (run: { map: string; nights: number; from?: number }, day: number): boolean =>
  run.map === todaysMap(day) && run.nights - ((run.from ?? 1) - 1) >= TODAYS_MAP.nights;

/**
 * A run's lessons (ADR-0176), each a gardener's until they have done it, on any map (their account's): day 1 waits for a
 * newcomer's first Sprinkler (ADR-0168 amended); from day 2 one who never placed `towers` towers in a run is asked for
 * one more each day (a sign on tonight's side, a nag, the key pointed at) until they have, and told at dawn where the
 * dead got in after a night that took `breach` of the Greenhouse's health.
 */
export const LESSONS = { towers: 3, breach: 0.2 } as const;

/** The towers day `day` asks a learner to have standing (ADR-0176): one more a day from day 2, up to `LESSONS.towers`. */
export const towersAsked = (day: number): number => (day < 2 ? 0 : Math.min(day, LESSONS.towers));

/**
 * A run's board by its size (ADR-0176: the most gardeners a night of it was scaled for): alone, or a party of 2-5. Each
 * size's best is kept too, so the party's board can split by size once it fills.
 */
export type ZombieGroup = 'solo' | 'party';
export const ZOMBIE_GROUPS: readonly ZombieGroup[] = ['solo', 'party'];
export const zombieGroupOf = (size: number): ZombieGroup => (size > 1 ? 'party' : 'solo');

export const NIGHTS = {
  dayTicks: ticks(60),
  /**
   * From day `shortDayFrom` a day lasts `shortDayTicks` (`dayTicks(n, from)`): the party has its defence by then, and a late
   * day spent waiting is the run's dead time. A run's first day is whole whatever its night (a dev jump's).
   */
  shortDayTicks: ticks(30),
  shortDayFrom: 6,
  /** The night's rising: `spawnBaseTicks` + `spawnPerNightTicks` a night after the first, at most `spawnMaxTicks`. */
  spawnBaseTicks: ticks(15),
  spawnPerNightTicks: ticks(1.5),
  spawnMaxTicks: ticks(25),
  /** Waves a night: `waves`, one more every `wavesEvery` nights, at most `maxWaves`; a wave rises within `waveTicks`. */
  waves: 2,
  wavesEvery: 5,
  maxWaves: 3,
  waveTicks: ticks(4),
  /** The first group rises this long into the night. */
  firstSpawnTicks: ticks(2),
  /** Zombies of one group rise this far apart. */
  groupGapTicks: ticks(0.25),
  /** Every budget of the script (`RUN`) times this: the harness's knob (`--patch 'NIGHTS.budgetScale=1.2'`). */
  budgetScale: 1,
  /**
   * A kind's first night (its `from`) is its night: it comes `spotlight` times as often as its `weight` says, so the
   * night's new question is asked loud and early.
   */
  spotlight: 4,
  /**
   * The fodder (`ZombieDef.fodder`: Shamblers, Runners) thins out of the mix by the night, `1 / (1 + fodderFade × (n -
   * 1))` of its weight: the late nights' crowds are the dead that ask something.
   */
  fodderFade: 0.15,
  /**
   * The dead come in more but pay less each (`bountyScale`, all but the bosses): a kill's bounty is `1 / (1 +
   * bountyFalloff × (n - 1))` of its def's on night `n` (at least 1), so a night's gold grows as it did when they came in
   * fewer and tougher, not with their numbers.
   */
  bountyFalloff: 0.18,
  /** Past the run's last night (`LONG.last`, endless): the budget grows by `endlessGrowth` a night, the dead's health by `endlessHp`. */
  endlessGrowth: 1.12,
  endlessHp: 1.2,
  /**
   * Endless nights bring `endlessBosses` bosses and one more every `endlessBossEvery` nights past the last (never fewer
   * than its), drawn by the night from every boss (no kind twice in a night while another is left), and the last night's
   * hearses and one more as often.
   */
  endlessBosses: 2,
  endlessBossEvery: 2,
  /** A zombie's knock shrinks by this much of night 1's a night, down to `knockFloor` of it (`knockScale`). */
  knockFalloff: 0.05,
  knockFloor: 0.3,
  damagePerNight: 0.06,
  /**
   * The party's damage on the dead grows by `defencePerNight` of night 1's a night (its guns, its towers, every way it
   * hurts them; `defenceScale`), while their health stays: the party outgrows the dead, and the dead come in more.
   */
  defencePerNight: 0.015,
  /**
   * What a gardener's gun that no longer reloads (`isEndless`) does on the dead, as a share of what it did: it fires the
   * time it used to spend reloading, so it hits a little softer and the nights stay as long as they were.
   */
  gunScale: 0.6,
  /**
   * The gates open by the night: `1 + n / gateEvery` of them (one on night 1, two from night 3), at most `maxGates`
   * (never all of a map's: the dead come from one side of the farm, another every night, `nightGates`).
   */
  gateEvery: 3,
  maxGates: 4,
  /** Party scaling: each gardener past the first adds this share of zombies. */
  partyCount: 0.5,
  /**
   * A party's questions' health by its size (alone first: `partyHp(p)`); a mate adds less the more there are (more
   * hands hunt them down faster, but the fifth adds less than the second).
   */
  partyHp: [1, 1.65, 2.05, 2.6, 3.2],
  /**
   * A gardener alone does every job at once (builds, mends, upgrades, shoots), with nobody to cover them or pick them
   * up: their run pays `soloGold` times its gold (the start purse, every dawn, every kill's bounty; `soloGold(p)`).
   */
  soloGold: 1.5,
  /** At most this many zombies stand at once; the rest wait at their gates. */
  maxAlive: 200,
  /**
   * The party's purse when a run starts and its dawn bonus (after night n: `dawnGold` + `dawnGoldPerNight` × n), each
   * shared by its players: alone all of it (`soloGold` times), a party `partyCount` times it split between them (`startGold`, `dawnGold`)
   * — the party's gold grows with its zombies, not with its size. Both are front-loaded (ADR-0227: the owner short of
   * gold on the gentle map), the first nights' purses most: the run opens on a day (ADR-0168 amended) with a purse whose
   * least share, a party of five's, is still a Sprinkler and three fences (ADR-0171).
   */
  startGold: 300,
  dawnGold: 100,
  dawnGoldPerNight: 10,
  /** The Greenhouse: its health, and the share of it regrown at each dawn. */
  baseHp: 2500,
  baseDawnHeal: 0.15,
  /**
   * The run's first nights teach (ADR-0176): up to night `graceNights` the dead take the Greenhouse down to `graceFloor`
   * of its health and no further. A party that only shoots bleeds and is told where they got in; its first lost run comes
   * once it has a run to lose.
   */
  graceNights: 3,
  graceFloor: 0.2,
  /** A gardener's respawn by night: `respawnTicks` + `respawnPerNight` × n, at most `respawnMaxTicks`. */
  respawnTicks: ticks(5),
  respawnPerNightTicks: ticks(0.3),
  respawnMaxTicks: ticks(10),
  /** A zombie climbs out of its grave for this long before it walks (it can be shot meanwhile). */
  riseTicks: ticks(0.6),
  /**
   * The run's second stage (ADR-0180) from this night: the dead walk round what is built, off the roads as on them, and
   * break through only where no way is left (`walksRound`); the siege kinds and the bosses go for builds all the same.
   */
  mazeFrom: 41,
} as const;

/** Do night `n`'s dead walk round builds (ADR-0180: the second stage on, `NIGHTS.mazeFrom`)? */
export const walksRound = (n: number): boolean => n >= NIGHTS.mazeFrom;

/**
 * A party picks up its fallen (ADR-0181): a gardener with a mate still on their feet goes down instead of dying, and
 * stays down, out of the fight and out of harm's way, until a mate has stood within `radius` px of them (centre to
 * centre) for `ticks` in all (one mate or five fill it alike; nobody in it, it waits), then gets up there with `hp` of
 * their health; or until dawn. A battle royale's squads pick up their downed in the same circle (ADR-0120 amended), in
 * their own time for their own health (`ROYALE.down`). Alone, or once nobody is left on their feet, a gardener dies and
 * gets up at the Greenhouse after the wait, as before (`NIGHTS.respawnTicks`).
 */
export const REVIVE = { radius: 26, ticks: ticks(3), hp: 0.5 } as const;

/** What share of a knock a zombie still takes on night `n`. */
export const knockScale = (n: number): number =>
  Math.max(NIGHTS.knockFloor, 1 - NIGHTS.knockFalloff * Math.max(0, n - 1));

/** How long night `n`'s zombies take to rise (its waves spread over it). */
export const spawnTicks = (n: number): number =>
  Math.min(NIGHTS.spawnMaxTicks, NIGHTS.spawnBaseTicks + NIGHTS.spawnPerNightTicks * Math.max(0, n - 1));

/** How many waves night `n` comes in. */
export const waveCount = (n: number): number =>
  Math.min(NIGHTS.maxWaves, NIGHTS.waves + Math.floor(Math.max(0, n - 1) / NIGHTS.wavesEvery));

/** How many nights past the run's last (`LONG.last`) night `n` is (0 up to it): endless. */
const endless = (n: number): number => Math.max(0, n - LONG.last);

/** How many nights of the long acts (ADR-0179: past the finale, up to the run's last) night `n` has behind it. */
const long = (n: number): number => Math.max(0, Math.min(n, LONG.last) - FINALE);

/**
 * How much tougher the dead are on night `n` than on the finale's: `LONG.hp` a long night (the night's headliners, `head`:
 * `LONG.headHp`), `endlessHp` an endless one.
 */
const toughness = (n: number, head = false): number =>
  (head && long(n) > 0 ? LONG.headStep * pow(LONG.headHp, long(n)) : pow(LONG.hp, long(n))) *
  pow(NIGHTS.endlessHp, endless(n));

/**
 * Night `n`'s threat budget before the party's share: the script's (`RUN`, `ACT_NIGHTS`), past the last `endlessGrowth` a
 * night more; from the second stage `HOLES.crowd` times that (ADR-0186: the graveyards keep their crowd, the holes bring
 * more).
 */
export const nightBudget = (n: number): number => {
  const budget = n <= FINALE ? (RUN[Math.max(1, n) - 1] as NightPlan).budget : LONG.budget * actNight(n).budget;
  return budget * NIGHTS.budgetScale * pow(NIGHTS.endlessGrowth, endless(n)) * (walksRound(n) ? HOLES.crowd : 1);
};

/** How many times the zombies a party of `players` meets (1 alone). */
export const partyCount = (players: number): number => 1 + NIGHTS.partyCount * Math.max(0, players - 1);

/** How many times the health each zombie has against a party of `players`. */
export const partyHp = (players: number): number => {
  const sizes = NIGHTS.partyHp;
  return sizes[Math.max(1, Math.min(sizes.length, Math.round(players))) - 1] as number;
};

/**
 * A zombie's health on night `n` against `players`, as a multiple of its def's: the same up to the finale, then
 * `toughness` (a hearse's, `heavy`, the headliners'); the crowd's (`fodder`) the same for any party.
 */
export const zombieHpScale = (n: number, players: number, fodder = false, heavy = false): number =>
  toughness(n, heavy) * (fodder ? 1 : partyHp(players));

/**
 * The party's damage to the dead on night `n` (its guns', its towers'), as a multiple of night 1's: `defencePerNight` up
 * to the finale, `LONG.defencePerNight` after.
 */
export const defenceScale = (n: number): number =>
  1 + NIGHTS.defencePerNight * Math.max(0, Math.min(n, FINALE) - 1) + LONG.defencePerNight * Math.max(0, n - FINALE);

/**
 * A zombie's bounty on night `n`, as a multiple of its def's (bosses keep theirs): `bountyFalloff` up to the finale (the
 * dead come in more there), then `LONG.bountyHp` of the toughness they gained in the long acts (a tougher dead pays more,
 * as a ceramic does).
 */
export const bountyScale = (n: number): number =>
  pow(pow(LONG.hp, long(n)), LONG.bountyHp) / (1 + NIGHTS.bountyFalloff * Math.max(0, Math.min(n, FINALE) - 1));

/** A zombie's damage on night `n`, as a multiple of its def's: `damagePerNight` up to the finale, `LONG.damagePerNight` after. */
export const zombieDamageScale = (n: number): number =>
  1 + NIGHTS.damagePerNight * Math.max(0, Math.min(n, FINALE) - 1) + LONG.damagePerNight * Math.max(0, n - FINALE);

/** What a run's gold pays a party of `players`, as a multiple of what it says: `NIGHTS.soloGold` alone, else 1. */
export const soloGold = (players: number): number => (players > 1 ? 1 : NIGHTS.soloGold);

/** A party of `players`' share of `gold` each: `partyCount` times it split between them (alone all of it, `soloGold`). */
const share = (gold: number, players: number): number =>
  Math.round((gold * partyCount(players) * soloGold(players)) / Math.max(1, players));

/** Gold each player of a party of `players` gets at the dawn after night `n`, on a map paying `scale` (`MapDef.dawnScale`). */
export const dawnGold = (n: number, players = 1, scale = 1): number =>
  share((NIGHTS.dawnGold + NIGHTS.dawnGoldPerNight * n) * scale, players);

/** Gold each player of a party of `players` starts a run from night `from` with: `runPurse`, shared out as a dawn's is. */
export const startGold = (players = 1, from = 1): number => share(runPurse(from), players);

/**
 * A boss's health on night `n` against `players`, as a multiple of its def's: it grows as the party's damage does
 * (`defenceScale`), so a boss stands as long against a gun whenever it comes; the party's cards and towers outgrow it.
 * In the long acts it toughens as a headliner (`LONG.headStep`, `headHp`): a long night's threat is its bosses and hearses.
 */
export const bossHpScale = (n: number, players: number): number =>
  defenceScale(n) * toughness(n, true) * partyHp(players);

/** How long a gardener killed during day/night `n` stays down. */
export const zombieRespawnTicks = (n: number): number =>
  Math.min(NIGHTS.respawnMaxTicks, NIGHTS.respawnTicks + NIGHTS.respawnPerNightTicks * Math.max(0, n - 1));

/** How many of a map's `gates` night `n` opens. */
export const gateCount = (n: number, gates: number): number =>
  Math.max(1, Math.min(gates, NIGHTS.maxGates, 1 + Math.floor(Math.max(0, n) / NIGHTS.gateEvery)));

// ─── Zombies ──────────────────────────────────────────────────────────────────────────────────────────

export const ZombieKind = {
  Shambler: 0,
  Runner: 1,
  Brute: 2,
  Spitter: 3,
  Bloater: 4,
  Crow: 5,
  Digger: 6,
  Horror: 7,
  Bearer: 8,
  Thresher: 9,
  Gravedigger: 10,
  Blight: 11,
  Priest: 12,
  /** A Moon Priest's altar: planted where it fell, never walks; fires rings of bolts while it stands. */
  Altar: 13,
  Lobber: 14,
  Deadeye: 15,
  /** The Buried King's head (ADR-0164): the run's mega boss, met in the Barrow after night `KING.night`. */
  King: 16,
  /** One of his fists: it comes down on a gardener, lies there a moment, and can be hit while it does. */
  Fist: 17,
  /** The Yoke (ADR-0165): the King's two plough oxen under one beam, the mega boss met in the Fallow after night `YOKE.night`. */
  Yoke: 18,
  /** One of its oxen, loose once the beam is broken: it charges on its own, and into the other. */
  Ox: 19,
  /** An ox that is down: it lies where it fell, in a gardener's way and in the other ox's. */
  Carcass: 20,
  /** A hearse full of the dead (ADR-0168): it rams down its lane; only its wheels can be hurt; stopped, its load climbs out. */
  Hearse: 21,
  /** A Shambler under a pail (ADR-0171): armoured (`ZombieDef.armoured`). */
  Bucket: 22,
  /** A pale fast dead (ADR-0171): shrouded (`ZombieDef.shrouded`). */
  Wisp: 23,
  /** A grave moth (ADR-0184): a flyer that flies to a Sunflower and smothers it (`ZOMBIE_MOTH`). */
  Moth: 24,
  /**
   * The weekly deck's variants (ADR-0226, `deckOf`): each comes first on a night the week deals it, never before night
   * `DECK.from`. A Shambler wrapped in moss: regrows whole unless it burns (`MOSS`).
   */
  Mossback: 25,
  /** Burnt through and walking: beams and fire do nothing, water hurts it triple (`CHARRED`). */
  Charred: 26,
  /** His smoke puts bees to sleep round him, his veil stops stings (`KEEPER`); he walks among a crowd. */
  Beekeeper: 27,
  /** Pumpkins and bees bounce off her parasol and the dead in its shade (`PARASOL`); she leads a crowd. */
  Parasol: 28,
  /** A corpse bloated into a gasbag, flying over everything with Bucket Heads hung under it (`BONE_BALLOON`). */
  Balloon: 29,
  /** Runs a route of its own with a sack of gold and digs out with it (`ROBBER`); killed, the sack is yours. */
  Robber: 30,
  /** Kneels at the first build in his way and blows it up when his fuse runs out (`SAPPER`). */
  Sapper: 31,
  /** Pulls the first fence in its way out and carries it off (`THIEF`); a Thorns fence it can't grip. */
  Thief: 32,
} as const;
export type ZombieKind = (typeof ZombieKind)[keyof typeof ZombieKind];

/**
 * A zombie armoured in front (the Coffin Bearer's lid, the Thresher's blades): a hit coming at it from within `arc`
 * radians of where it faces is stopped whole (a `parry`), one from underfoot (fire, a puddle, its rot) never is; it turns
 * to face its way at most `turn` radians a second, so a gardener circling it gets round; a hit from within `arc` of
 * straight behind it does `back` times the damage, or, armoured at the back too (`rear`: a hearse, its body all steel but its
 * wheels), nothing: only a hit from its side gets through.
 */
export interface ZombieGuard {
  readonly arc: number;
  readonly turn: number;
  readonly back: number;
  readonly rear?: true;
}

export interface ZombieDef {
  readonly kind: ZombieKind;
  readonly key:
    | 'shambler'
    | 'runner'
    | 'brute'
    | 'spitter'
    | 'bloater'
    | 'crow'
    | 'digger'
    | 'horror'
    | 'bearer'
    | 'thresher'
    | 'gravedigger'
    | 'blight'
    | 'priest'
    | 'altar'
    | 'lobber'
    | 'deadeye'
    | 'king'
    | 'fist'
    | 'yoke'
    | 'ox'
    | 'carcass'
    | 'hearse'
    | 'bucket'
    | 'wisp'
    | 'moth'
    | 'mossback'
    | 'charred'
    | 'beekeeper'
    | 'parasol'
    | 'balloon'
    | 'robber'
    | 'sapper'
    | 'thief';
  readonly name: string;
  /** What it does, for the HUD's night card (a short sentence). */
  readonly desc: string;
  /** Night 1's health (scaled by the night and the party, `zombieHpScale`). */
  readonly maxHp: number;
  /** px/s. */
  readonly speed: number;
  readonly bodyRadius: number;
  readonly hurtRadius: number;
  /** Per swing (scaled by the night, `zombieDamageScale`), within `reach` px edge to edge, every `attackTicks`. */
  readonly damage: number;
  readonly reach: number;
  readonly attackTicks: number;
  /** Its damage to builds and the Greenhouse is `damage` × this. */
  readonly vsBuild: number;
  /** A gardener within this many px (edge to edge, in sight) is chased instead of the Greenhouse; 0 = never. */
  readonly aggro: number;
  /**
   * Its kill's gold, the party's whoever (or whatever tower) killed it: split evenly between the run's players, each share
   * grown by its player's Greed (`bountyScale` by the night).
   */
  readonly bounty: number;
  /** What it takes from the night's budget, how often it is picked, the first night it comes, its group size. */
  readonly cost: number;
  readonly weight: number;
  readonly from: number;
  readonly group: readonly [number, number];
  /** Flies over everything (no body; fences, hedges and water don't stop it). */
  readonly flying: boolean;
  /** The crowd (`NIGHTS.fodderFade`): it thins out of the mix by the night. */
  readonly fodder?: true;
  /** Nothing throws it (a hearse), as nothing throws a boss. */
  readonly heavy?: true;
  /** A boss (`nightBosses`): comes on boss nights only, its health on its own curve (`bossHpScale`). */
  readonly boss: boolean;
  /**
   * A siege kind (ADR-0180): from the second stage, when the rest of the dead walk round builds (`walksRound`), it still
   * walks through them, breaking what is in its way, as every boss does: the question that tests a maze.
   */
  readonly siege?: true;
  /** No boss, but its health on the bosses' curve all the same (a Moon Altar: its Priest's). */
  readonly bossHp?: boolean;
  /** Armoured in front (`ZombieGuard`). */
  readonly guard?: ZombieGuard;
  /**
   * A part of a mega boss (the Buried King's, ADR-0164; the Yoke's, ADR-0165): no night brings it, the dead's brains
   * and the towers leave it alone and nothing throws or dazes it (its own system moves it: `kingSystem`, `yokeSystem`),
   * and its health is a mega boss's (`kingHp`). The King's can be hit only while they lie on the ground.
   */
  readonly king?: true;
  /**
   * Armoured (ADR-0171: a Bucket Head's pail): bullets, water and stings clang off it whole (a `parry`); blasts, beams,
   * fire and a gardener's Piercing Rounds hurt it, and a Heavy Shell's blast knocks the armour off for good
   * (`EntityFlag.Cracked`).
   */
  readonly armoured?: true;
  /**
   * Shrouded (ADR-0171: a Wisp): towers and their bees can't see it unless a Sunflower's Daylight lights it
   * (`EntityFlag.Revealed`) or they see Wisps (`TowerStats.wisps`, ADR-0227); gardeners see and shoot it all the same.
   */
  readonly shrouded?: true;
  /**
   * One of the weekly deck's variants (ADR-0226): its `from` is the night the week's deck deals it (`deckOf`), never a
   * night of its own; without a deck it never comes.
   */
  readonly variant?: true;
  /** It leads a crowd (ADR-0226: a Beekeeper, a Parasol): `count` of `kind` rise right behind it, its `cost` paying for them. */
  readonly escort?: { readonly kind: ZombieKind; readonly count: number };
  /** Its bounty is a prize (a Grave Robber's sack, ADR-0226): the night never shrinks it. */
  readonly prize?: true;
  /** While the farm is hell (ADR-0187, `hellOn`) it comes this many times as often (the Charred: hell's commonest). */
  readonly hell?: number;
  /** Minimap / UI colour. */
  readonly color: number;
}

/** A variant's own `from` (ADR-0226): none; the week's deck deals it its first night (`firstNight`). */
export const DEALT = 0xffff;

export const ZOMBIES: readonly ZombieDef[] = [
  {
    kind: ZombieKind.Shambler,
    key: 'shambler',
    name: 'Shambler',
    desc: 'Slow and stubborn. There are always more.',
    maxHp: 55,
    speed: 22,
    bodyRadius: 4.5,
    hurtRadius: 6,
    damage: 8,
    reach: 4,
    attackTicks: ticks(1.1),
    vsBuild: 1,
    aggro: 40,
    bounty: 3,
    cost: 1,
    weight: 10,
    from: 1,
    group: [3, 6],
    flying: false,
    fodder: true,
    boss: false,
    color: 0x63c74d,
  },
  {
    kind: ZombieKind.Runner,
    key: 'runner',
    name: 'Runner',
    desc: 'Fast and frail. Goes for gardeners.',
    maxHp: 28,
    speed: 56,
    bodyRadius: 4,
    hurtRadius: 5.5,
    damage: 5,
    reach: 4,
    attackTicks: ticks(0.7),
    vsBuild: 0.6,
    aggro: 72,
    bounty: 2,
    cost: 1,
    weight: 6,
    from: 2,
    group: [3, 5],
    flying: false,
    fodder: true,
    boss: false,
    color: 0xfeae34,
  },
  {
    kind: ZombieKind.Brute,
    key: 'brute',
    name: 'Brute',
    desc: 'Huge and slow. Smashes builds, ignores you.',
    maxHp: 300,
    speed: 18,
    bodyRadius: 8,
    hurtRadius: 10,
    damage: 30,
    reach: 5,
    attackTicks: ticks(1.6),
    vsBuild: 1.8,
    aggro: 0,
    bounty: 18,
    cost: 7,
    weight: 3,
    from: 4,
    group: [1, 2],
    flying: false,
    boss: false,
    siege: true,
    color: 0xb86f50,
  },
  {
    kind: ZombieKind.Spitter,
    key: 'spitter',
    name: 'Spitter',
    desc: 'Outranges most towers. Hunt it down.',
    maxHp: 60,
    speed: 22,
    bodyRadius: 4.5,
    hurtRadius: 6,
    damage: 12,
    reach: 4,
    attackTicks: ticks(2.5),
    vsBuild: 1.2,
    aggro: 150,
    bounty: 6,
    cost: 3,
    weight: 3,
    from: 6,
    group: [1, 3],
    flying: false,
    boss: false,
    siege: true,
    color: 0x2ce8f5,
  },
  {
    kind: ZombieKind.Bloater,
    key: 'bloater',
    name: 'Bloater',
    desc: 'Bursts when it dies or touches something. Hurts everyone.',
    maxHp: 110,
    speed: 17,
    bodyRadius: 6,
    hurtRadius: 8,
    damage: 0,
    reach: 3,
    attackTicks: ticks(1),
    vsBuild: 2,
    aggro: 40,
    bounty: 7,
    cost: 3,
    weight: 3,
    from: 17,
    group: [1, 2],
    flying: false,
    boss: false,
    color: 0xb55088,
  },
  {
    kind: ZombieKind.Crow,
    key: 'crow',
    name: 'Crow',
    desc: 'Flies over walls in flocks. Pecks gardeners. Bees, guns and some upgrades reach it.',
    maxHp: 16,
    speed: 70,
    bodyRadius: 3,
    hurtRadius: 5,
    // ADR-0184: when only bees and guns reached a crow, it came to peck softer (4 before).
    damage: 3,
    reach: 3,
    attackTicks: ticks(0.8),
    vsBuild: 0.5,
    aggro: 200,
    bounty: 2,
    cost: 0.5,
    weight: 4,
    from: 3,
    group: [4, 7],
    flying: true,
    boss: false,
    color: 0x5a6988,
  },
  {
    kind: ZombieKind.Digger,
    key: 'digger',
    name: 'Digger',
    desc: 'Tunnels to your door and bursts up under it. Wet ground brings it up early.',
    maxHp: 80,
    speed: 20,
    bodyRadius: 5,
    hurtRadius: 7,
    damage: 10,
    reach: 4,
    attackTicks: ticks(1),
    vsBuild: 1.5,
    aggro: 40,
    bounty: 7,
    cost: 3,
    weight: 3,
    from: 8,
    group: [1, 2],
    flying: false,
    boss: false,
    color: 0x9e6743,
  },
  {
    kind: ZombieKind.Horror,
    key: 'horror',
    name: 'Harvest Horror',
    desc: 'The boss. Flattens builds and calls up the dead.',
    maxHp: 1500,
    speed: 13,
    bodyRadius: 12,
    hurtRadius: 15,
    damage: 40,
    reach: 6,
    attackTicks: ticks(1.8),
    vsBuild: 2,
    aggro: 60,
    bounty: 110,
    cost: 40,
    weight: 0,
    from: 5,
    group: [1, 1],
    flying: false,
    boss: true,
    color: 0xf77622,
  },
  {
    kind: ZombieKind.Bearer,
    key: 'bearer',
    name: 'Coffin Bearer',
    desc: 'Its coffin lid stops shots from the front. Hit it from behind.',
    maxHp: 150,
    speed: 18,
    bodyRadius: 5.5,
    hurtRadius: 7,
    damage: 12,
    reach: 4,
    attackTicks: ticks(1.2),
    vsBuild: 1.5,
    aggro: 40,
    bounty: 8,
    cost: 4,
    weight: 3,
    from: 11,
    group: [1, 2],
    flying: false,
    boss: false,
    guard: { arc: 1.1, turn: 1.6, back: 1 },
    color: 0xe4a672,
  },
  {
    kind: ZombieKind.Thresher,
    key: 'thresher',
    name: 'Thresher',
    desc: 'Hunts down your builds and mows them. Steel front: hit its engine.',
    maxHp: 1500,
    speed: 13,
    bodyRadius: 12,
    hurtRadius: 15,
    damage: 16,
    reach: 6,
    attackTicks: ticks(0.25),
    vsBuild: 2.5,
    aggro: 0,
    bounty: 120,
    cost: 40,
    weight: 0,
    from: 9,
    group: [1, 1],
    flying: false,
    boss: true,
    guard: { arc: 0.8, turn: 0.9, back: 2 },
    color: 0xbe4a2f,
  },
  {
    kind: ZombieKind.Gravedigger,
    key: 'gravedigger',
    name: 'Gravedigger',
    desc: 'Heaves her shovel in a wide arc now and then. Step out of the cone.',
    maxHp: 1500,
    speed: 14,
    bodyRadius: 10,
    hurtRadius: 13,
    damage: 28,
    reach: 6,
    attackTicks: ticks(1.4),
    vsBuild: 2,
    aggro: 50,
    bounty: 120,
    cost: 40,
    weight: 0,
    from: 14,
    group: [1, 1],
    flying: false,
    boss: true,
    color: 0xc28569,
  },
  {
    kind: ZombieKind.Blight,
    key: 'blight',
    name: 'Blight',
    desc: 'Takes root and spreads rot that eats your builds. Kill it fast.',
    maxHp: 2600,
    speed: 14,
    bodyRadius: 11,
    hurtRadius: 14,
    damage: 20,
    reach: 8,
    attackTicks: ticks(1.2),
    vsBuild: 1,
    aggro: 0,
    bounty: 135,
    cost: 40,
    weight: 0,
    from: 16,
    group: [1, 1],
    flying: false,
    boss: true,
    color: 0x3e8948,
  },
  {
    kind: ZombieKind.Priest,
    key: 'priest',
    name: 'Moon Priest',
    desc: 'Plants altars near your Greenhouse. Nothing hurts it while one stands.',
    maxHp: 1500,
    speed: 14,
    bodyRadius: 9,
    hurtRadius: 12,
    damage: 24,
    reach: 6,
    attackTicks: ticks(1.3),
    vsBuild: 2,
    aggro: 50,
    bounty: 125,
    cost: 40,
    weight: 0,
    from: 18,
    group: [1, 1],
    flying: false,
    boss: true,
    color: 0x68386c,
  },
  {
    kind: ZombieKind.Altar,
    key: 'altar',
    name: 'Moon Altar',
    desc: "Fires rings of bolts. Its Priest can't be hurt while it stands.",
    maxHp: 200,
    speed: 0,
    bodyRadius: 6,
    hurtRadius: 9,
    // A bolt's (× the night), a ring every `attackTicks`.
    damage: 12,
    reach: 0,
    attackTicks: ticks(2.2),
    vsBuild: 1.5,
    aggro: 0,
    bounty: 30,
    cost: 0,
    weight: 0,
    from: 18,
    group: [1, 1],
    flying: false,
    boss: false,
    bossHp: true,
    color: 0xf6757a,
  },
  {
    kind: ZombieKind.Lobber,
    key: 'lobber',
    name: 'Lobber',
    desc: 'Lobs rotten pumpkins over your fences. Step out of the ring.',
    maxHp: 70,
    speed: 20,
    bodyRadius: 4.5,
    hurtRadius: 6,
    // A pumpkin's burst (× the night), a throw every `attackTicks`.
    damage: 13,
    reach: 4,
    attackTicks: ticks(3),
    vsBuild: 1.5,
    aggro: 0,
    bounty: 7,
    cost: 3,
    weight: 1,
    from: 19,
    group: [1, 2],
    flying: false,
    boss: false,
    siege: true,
    color: 0xd77643,
  },
  {
    kind: ZombieKind.Deadeye,
    key: 'deadeye',
    name: 'Deadeye',
    desc: 'Aims along a red line, then one shot through everything on it. Step off the line.',
    maxHp: 75,
    speed: 20,
    bodyRadius: 4.5,
    hurtRadius: 6,
    // Its shot (× the night), every `attackTicks` at most.
    damage: 30,
    reach: 4,
    attackTicks: ticks(4),
    vsBuild: 1.5,
    aggro: 0,
    bounty: 9,
    cost: 4,
    weight: 1,
    from: 15,
    group: [1, 1],
    flying: false,
    boss: false,
    siege: true,
    color: 0xa22633,
  },
  {
    kind: ZombieKind.King,
    key: 'king',
    name: 'Buried King',
    desc: 'Dodge what he throws at you. When his head comes down, hit it.',
    maxHp: 1250,
    speed: 0,
    bodyRadius: 0,
    hurtRadius: 44,
    // His head coming down.
    damage: 40,
    reach: 0,
    attackTicks: ticks(1),
    vsBuild: 1,
    aggro: 0,
    bounty: 300,
    cost: 0,
    weight: 0,
    from: 10,
    group: [1, 1],
    flying: false,
    boss: false,
    king: true,
    color: 0xc0cbdc,
  },
  {
    kind: ZombieKind.Fist,
    key: 'fist',
    name: "King's Fist",
    desc: 'Its shadow shows where it lands. Step out, then hit it while it lies there.',
    maxHp: 440,
    speed: 0,
    bodyRadius: 0,
    hurtRadius: 15,
    // A fist coming down.
    damage: 30,
    reach: 0,
    attackTicks: ticks(1),
    vsBuild: 1,
    aggro: 0,
    bounty: 60,
    cost: 0,
    weight: 0,
    from: 10,
    group: [1, 1],
    flying: false,
    boss: false,
    king: true,
    color: 0xc0cbdc,
  },
  {
    kind: ZombieKind.Yoke,
    key: 'yoke',
    name: 'The Yoke',
    desc: 'Step out of their charge. They stop at the edge: hit them from behind.',
    maxHp: 2160,
    speed: 40,
    bodyRadius: 0,
    hurtRadius: 44,
    // Run down by the pair.
    damage: 46,
    reach: 0,
    attackTicks: ticks(1),
    vsBuild: 1,
    aggro: 0,
    bounty: 0,
    cost: 0,
    weight: 0,
    from: 20,
    group: [1, 1],
    flying: false,
    boss: false,
    guard: { arc: 1, turn: 2.6, back: 2 },
    king: true,
    color: 0x8b9bb4,
  },
  {
    kind: ZombieKind.Ox,
    key: 'ox',
    name: 'Plow Ox',
    desc: 'Steel front. Stand between the two and step out: they run into each other.',
    maxHp: 3600,
    speed: 46,
    bodyRadius: 0,
    hurtRadius: 22,
    // Run down by one of them.
    damage: 38,
    reach: 0,
    attackTicks: ticks(1),
    vsBuild: 1,
    aggro: 0,
    bounty: 150,
    cost: 0,
    weight: 0,
    from: 20,
    group: [1, 1],
    flying: false,
    boss: false,
    guard: { arc: 1, turn: 2.6, back: 2 },
    king: true,
    color: 0x8b9bb4,
  },
  {
    kind: ZombieKind.Carcass,
    key: 'carcass',
    name: 'Fallen Ox',
    desc: 'It lies where it fell. The other ox runs into it too.',
    maxHp: 1,
    speed: 0,
    bodyRadius: 0,
    hurtRadius: 0,
    damage: 0,
    reach: 0,
    attackTicks: ticks(1),
    vsBuild: 1,
    aggro: 0,
    bounty: 0,
    cost: 0,
    weight: 0,
    from: 20,
    group: [1, 1],
    flying: false,
    boss: false,
    king: true,
    color: 0x5a6988,
  },
  {
    kind: ZombieKind.Hearse,
    key: 'hearse',
    name: 'Hearse',
    desc: 'Rams through fences with a load of the dead. Only its wheels can be hurt: shoot it from the side.',
    // Its wheels'.
    maxHp: 520,
    speed: 30,
    bodyRadius: 10,
    hurtRadius: 13,
    // Its ram (× the night), every `attackTicks` at what is in front of it.
    damage: 24,
    reach: 4,
    attackTicks: ticks(0.5),
    vsBuild: 4,
    aggro: 0,
    bounty: 30,
    cost: 16,
    weight: 0,
    from: 13,
    group: [1, 1],
    flying: false,
    boss: false,
    guard: { arc: 0.9, turn: 1.4, back: 1, rear: true },
    heavy: true,
    color: 0xc0cbdc,
  },
  {
    kind: ZombieKind.Bucket,
    key: 'bucket',
    name: 'Bucket Head',
    desc: 'A metal pail on its head: bullets bounce off. Blasts, beams and fire hurt it.',
    maxHp: 95,
    speed: 20,
    bodyRadius: 4.5,
    hurtRadius: 6,
    damage: 9,
    reach: 4,
    attackTicks: ticks(1.1),
    vsBuild: 1.2,
    aggro: 40,
    bounty: 6,
    cost: 3,
    weight: 4,
    from: 7,
    group: [2, 4],
    flying: false,
    boss: false,
    armoured: true,
    color: 0x8b9bb4,
  },
  {
    kind: ZombieKind.Wisp,
    key: 'wisp',
    name: 'Wisp',
    desc: "Towers can't see it without the right upgrade. You can.",
    maxHp: 45,
    speed: 34,
    bodyRadius: 4,
    hurtRadius: 5.5,
    damage: 7,
    reach: 4,
    attackTicks: ticks(0.8),
    vsBuild: 1,
    aggro: 60,
    bounty: 4,
    cost: 2,
    weight: 4,
    from: 12,
    group: [3, 5],
    flying: false,
    boss: false,
    shrouded: true,
    color: 0xdff6f5,
  },
  {
    kind: ZombieKind.Moth,
    key: 'moth',
    name: 'Moth',
    desc: "Drawn to Sunflowers: smothers them so they can't fire. Bees and guns swat it.",
    maxHp: 16,
    speed: 46,
    bodyRadius: 3,
    hurtRadius: 5,
    damage: 3,
    reach: 2,
    attackTicks: ticks(1),
    vsBuild: 0.4,
    aggro: 0,
    bounty: 3,
    cost: 1,
    weight: 4,
    from: 10,
    group: [3, 5],
    flying: true,
    boss: false,
    color: 0xe8b796,
  },
  // ─── The weekly deck's variants (ADR-0226): each first comes on the night the week deals it (`deckOf`) ───
  {
    kind: ZombieKind.Mossback,
    key: 'mossback',
    name: 'Mossback',
    desc: 'Regrows whole 2 s after its last hit. Fire and poison stop the moss.',
    maxHp: 130,
    speed: 22,
    bodyRadius: 4.5,
    hurtRadius: 6,
    damage: 8,
    reach: 4,
    attackTicks: ticks(1.1),
    vsBuild: 1,
    aggro: 40,
    bounty: 5,
    cost: 3,
    weight: 3,
    from: DEALT,
    group: [2, 4],
    flying: false,
    boss: false,
    variant: true,
    color: 0x3e8948,
  },
  {
    kind: ZombieKind.Charred,
    key: 'charred',
    name: 'Charred',
    desc: 'Beams and fire do nothing. Water sizzles it for triple.',
    maxHp: 120,
    speed: 22,
    bodyRadius: 4.5,
    hurtRadius: 6,
    damage: 9,
    reach: 4,
    attackTicks: ticks(1.1),
    vsBuild: 1.2,
    aggro: 40,
    bounty: 5,
    cost: 3,
    weight: 3,
    from: DEALT,
    group: [2, 4],
    flying: false,
    boss: false,
    variant: true,
    hell: 2,
    color: 0x3e2731,
  },
  {
    kind: ZombieKind.Beekeeper,
    key: 'beekeeper',
    name: 'Beekeeper',
    desc: 'His smoke puts bees to sleep, his veil stops stings. Kill him first.',
    maxHp: 90,
    speed: 22,
    bodyRadius: 4.5,
    hurtRadius: 6,
    damage: 8,
    reach: 4,
    attackTicks: ticks(1.1),
    vsBuild: 1,
    aggro: 40,
    bounty: 12,
    cost: 8,
    weight: 2,
    from: DEALT,
    group: [1, 1],
    flying: false,
    boss: false,
    variant: true,
    escort: { kind: ZombieKind.Shambler, count: 5 },
    color: 0xfee761,
  },
  {
    kind: ZombieKind.Parasol,
    key: 'parasol',
    name: 'Parasol',
    desc: 'Pumpkins and bees bounce off her parasol and the dead in its shade. Beam her.',
    maxHp: 110,
    speed: 22,
    bodyRadius: 4.5,
    hurtRadius: 6,
    damage: 8,
    reach: 4,
    attackTicks: ticks(1.1),
    vsBuild: 1,
    aggro: 40,
    bounty: 12,
    cost: 8,
    weight: 2,
    from: DEALT,
    group: [1, 1],
    flying: false,
    boss: false,
    variant: true,
    escort: { kind: ZombieKind.Shambler, count: 4 },
    color: 0xf6757a,
  },
  {
    kind: ZombieKind.Balloon,
    key: 'balloon',
    name: 'Bone Balloon',
    desc: 'Floats over every wall with four Bucket Heads under it. Bees, guns and some upgrades reach it.',
    maxHp: 470,
    speed: 20,
    bodyRadius: 8,
    hurtRadius: 16,
    // Coming down on the Greenhouse (× the night, `BONE_BALLOON.crash` times).
    damage: 20,
    reach: 4,
    attackTicks: ticks(1),
    vsBuild: 2,
    aggro: 0,
    bounty: 25,
    cost: 0,
    weight: 0,
    from: DEALT,
    group: [1, 1],
    flying: true,
    boss: false,
    variant: true,
    color: 0xc0cbdc,
  },
  {
    kind: ZombieKind.Robber,
    key: 'robber',
    name: 'Grave Robber',
    desc: 'Runs off with a sack of gold and digs out in 20 s. Kill it for the sack.',
    maxHp: 240,
    speed: 58,
    bodyRadius: 4,
    hurtRadius: 5.5,
    damage: 0,
    reach: 4,
    attackTicks: ticks(1),
    vsBuild: 1,
    aggro: 0,
    bounty: 500,
    cost: 0,
    weight: 0,
    from: DEALT,
    group: [1, 1],
    flying: false,
    boss: false,
    variant: true,
    prize: true,
    color: 0xfeae34,
  },
  {
    kind: ZombieKind.Sapper,
    key: 'sapper',
    name: 'Sapper',
    desc: 'Lights a keg at the first build in his way. 5 s to shoot him or it blows.',
    maxHp: 110,
    speed: 24,
    bodyRadius: 4.5,
    hurtRadius: 6,
    damage: 8,
    reach: 4,
    attackTicks: ticks(1.1),
    vsBuild: 1,
    aggro: 0,
    bounty: 9,
    cost: 5,
    weight: 2,
    from: DEALT,
    group: [1, 1],
    flying: false,
    boss: false,
    siege: true,
    variant: true,
    color: 0xf77622,
  },
  {
    kind: ZombieKind.Thief,
    key: 'thief',
    name: 'Fence Thief',
    desc: 'Pulls a fence out of your walls and carries it off. Thorns it can’t grip.',
    maxHp: 160,
    speed: 26,
    bodyRadius: 5,
    hurtRadius: 7,
    damage: 8,
    reach: 4,
    attackTicks: ticks(1.1),
    vsBuild: 1,
    aggro: 0,
    bounty: 9,
    cost: 5,
    weight: 2,
    from: DEALT,
    group: [1, 1],
    flying: false,
    boss: false,
    siege: true,
    variant: true,
    color: 0x5a6988,
  },
];

export function zombieDef(kind: number): ZombieDef {
  return ZOMBIES[kind] ?? (ZOMBIES[0] as ZombieDef);
}

/**
 * A night of the run's script (`RUN`): its threat budget alone (`nightBudget`), the bosses that come with its dead, and
 * how many hearses (`HEARSE`: set pieces, never drawn by weight; a party's `partyCount` times as many).
 */
export interface NightPlan {
  readonly budget: number;
  readonly bosses?: readonly ZombieKind[];
  readonly hearses?: number;
}

/**
 * The run (ADR-0168): the same nights every run, a new question every night or two (a kind on its `from` night, a
 * boss), the Buried King after night 10 (`KING.night`) as the first act's exam and the Yoke after the last
 * (`YOKE.night`, `FINALE`) as the run's: a run that beats it is won and goes on, endless. Each act opens calm (the night
 * after the King comes in fewer than his did) and builds to its exam. Its last nights come in half the crowds they did
 * (ADR-0179 amended: compact nights), in a faster rising (`NIGHTS.spawnMaxTicks`): what makes them harder is what they
 * bring, their new kind, bosses and hearses.
 */
const K = ZombieKind;
export const RUN: readonly NightPlan[] = [
  { budget: 20 }, // 1: Shamblers
  { budget: 28 }, // 2: Runners
  { budget: 40 }, // 3: Crows
  { budget: 60 }, // 4: Brutes
  { budget: 75, bosses: [K.Horror] }, // 5: the Harvest Horror
  { budget: 140 }, // 6: Spitters
  { budget: 200 }, // 7: Bucket Heads
  { budget: 290 }, // 8: Diggers
  { budget: 360, bosses: [K.Thresher] }, // 9: the Thresher
  { budget: 420 }, // 10: Moths, the Buried King after it
  { budget: 400 }, // 11: Coffin Bearers
  { budget: 540, bosses: [K.Horror] }, // 12: Wisps
  { budget: 680, hearses: 3 }, // 13: the Hearse
  { budget: 800, bosses: [K.Gravedigger], hearses: 1 }, // 14: the Gravedigger
  { budget: 880, bosses: [K.Thresher], hearses: 2 }, // 15: Deadeyes
  { budget: 1050, bosses: [K.Blight], hearses: 2 }, // 16: the Blight
  { budget: 1350, bosses: [K.Horror, K.Gravedigger], hearses: 3 }, // 17: Bloaters
  { budget: 1600, bosses: [K.Priest], hearses: 2 }, // 18: the Moon Priest
  { budget: 1800, bosses: [K.Thresher, K.Blight], hearses: 3 }, // 19: Lobbers
  { budget: 2000, hearses: 4 }, // 20: the Yoke after it
];

/** The run's last scripted night: the Yoke's (ADR-0165). Its dawn wins the run; past it the nights are endless. */
export const FINALE = RUN.length;

/**
 * The long run (ADR-0179): past the first act's finale four more acts of `ACT` nights take the run to night `last`, each
 * built the same way (`ACT_NIGHTS`) round its own rushes (`ACTS`) and ending on a mega boss (`MEGA`); past `last` the
 * nights are endless (`NIGHTS.endlessGrowth`, `endlessHp`).
 * - `budget`: an act's full night (an `ACT_NIGHTS` budget of 1) alone, half the finale's (ADR-0179 amended: compact
 *   nights, a quarter of the crowd it was). The dead come in no more than that from act to act: they toughen instead, so
 *   a night's crowd stays one a screen (and a server) holds, and a night lasts about a minute.
 * - `hp`: the crowd's health grows this much a long night, compounding: an act's dead are about 2.7 times the last's.
 *   Gentle, so a party that plays better lasts many nights longer, not one or two (the old endless grew a night's threat
 *   1.34 times a night: every run past the finale ended within a few nights of the others).
 * - `headStep`, `headHp`: the headliners' (the bosses, their altars, the hearses) is `headStep` times the finale's from
 *   the first long night and grows `headHp` a long night after: a long night's threat is what it brings, not how many
 *   (a crowd one a screen holds and a gun pops), and the second act's bosses are fights from its first night.
 *   ADR-0227's towers (a Sunflower twice as hard on them, crosspaths) left a run past night 20 next to nothing to lose
 *   before night 40 at 5 (`plaza`, 72 seeds, Twin Hollows alone: 46 of the 50 past night 20 got there, most with the
 *   Greenhouse untouched); at 9 about a third do (16 of 54), as before ADR-0227 (5 of 16), the parade of night 30 ending
 *   a fifth of the runs (at 12, two in five), and the third act ends Willow Bend's runs where it did (to night 60, 12
 *   seeds alone: 43.2 nights, 42.2 before ADR-0227, 47.9 at 5).
 * - `bountyHp`: a kill pays the finale's bounty times this power of the toughness it gained (a tougher dead pays more, as
 *   a ceramic does): the party's gold grows as `hp ** bountyHp` a night, its threat as `hp`, so a night's threat over
 *   what the party holds rises about `hp ** ((1 - bountyHp) × ACT)` an act (1.63 at 1.05 and 0.5): each act harder
 *   than the last for the same play, and a party that plays better crosses it later.
 * - `defencePerNight`: the party's damage grows this share of night 1's a long night by itself (`defenceScale`); none,
 *   so what a party holds in the long acts is what it earned and chose, never a gift every party gets alike.
 * - `damagePerNight`: the dead's blows grow this share of night 1's a long night (half the first act's).
 * - `rush`: a rush night's kinds (`ACTS`) come this many times as often as their weight says (the crowd's never thinned).
 * - `funeral`: the hearses a rush of hearses brings more, alone (a party's `partyCount` times as many).
 * - `worth`: a run from a long night (`runPurse`) starts with the last worth `PURSE.worth` lists and this
 *   much more a night past it (endless nights: `endlessGrowth` times more): the second act's, measured (`--worth`,
 *   `plaza` alone on Moonlit Farm, 72 seeds, ADR-0227: 14,000 at night 21's dusk, 34,400 at night 40's).
 */
export const LONG = {
  last: 100,
  budget: 1000,
  hp: 1.05,
  headStep: 9,
  headHp: 1.03,
  bountyHp: 0.5,
  defencePerNight: 0,
  damagePerNight: 0.03,
  rush: 6,
  funeral: 3,
  worth: 1100,
} as const;

/** The nights an act lasts (ADR-0179): the first is the script's (`RUN`), the rest the long run's. */
export const ACT = 20;

/** The act night `n` is in: 1 the script's, up to `LONG.last / ACT` (endless nights are the last's). */
export const actOf = (n: number): number => Math.max(1, Math.ceil(Math.min(n, LONG.last) / ACT));

/**
 * A night of a long act by its place in it (ADR-0179): its budget as a share of `LONG.budget`, the bosses it brings
 * (one more each act past the second; `parade`: one of every boss), its hearses (one more each act past the second as
 * well), and which of its act's rushes (`ACTS`) it is.
 */
export interface ActNight {
  readonly budget: number;
  readonly bosses?: number;
  readonly parade?: true;
  readonly hearses?: number;
  readonly rush?: number;
}

/**
 * Every long act's nights in order (ADR-0179, amended: compact nights): four nights a round, built up and let go as a
 * director would (Left 4 Dead's): a calm night, a night of hearses, a night of bosses, then a peak, its act's rush with
 * bosses and hearses at once, the chaos the act is played for; halfway every boss at once (a parade), and its last night
 * the exam's, the mega boss after it.
 */
export const ACT_NIGHTS: readonly ActNight[] = [
  { budget: 0.6 }, // 1: calm after the exam
  { budget: 0.7, hearses: 1 },
  { budget: 0.75, bosses: 1 },
  { budget: 0.85, rush: 0, bosses: 1, hearses: 1 }, // 4: a peak
  { budget: 0.65 }, // 5: calm
  { budget: 0.75, hearses: 1 },
  { budget: 0.8, bosses: 1 },
  { budget: 0.9, rush: 1, bosses: 1, hearses: 2 }, // 8: a peak
  { budget: 0.7 }, // 9: calm
  { budget: 0.8, parade: true }, // 10: every boss at once
  { budget: 0.8, hearses: 2 },
  { budget: 0.95, rush: 2, bosses: 2, hearses: 1 }, // 12: a peak
  { budget: 0.7 }, // 13: calm
  { budget: 0.85, hearses: 2 },
  { budget: 0.9, bosses: 2 },
  { budget: 1, rush: 3, bosses: 2, hearses: 2 }, // 16: a peak
  { budget: 0.75 }, // 17: calm
  { budget: 0.95, hearses: 2 },
  { budget: 1.05, bosses: 2, hearses: 2 },
  { budget: 1.1, hearses: 3 }, // 20: the exam's night, its mega boss after it
];

/**
 * The long acts (ADR-0179), the second first: the kinds each one's rush nights bring `LONG.rush` times as often (a
 * Hearse: `LONG.funeral` hearses more). The second asks the first act's questions one at a time, the third the rest,
 * the fourth two at once, the fifth three and four.
 */
export const ACTS: readonly { readonly rushes: readonly (readonly ZombieKind[])[] }[] = [
  { rushes: [[K.Crow, K.Moth], [K.Bucket, K.Bearer], [K.Wisp], [K.Spitter, K.Lobber, K.Deadeye]] },
  { rushes: [[K.Runner, K.Wisp], [K.Brute, K.Bloater], [K.Digger], [K.Hearse]] },
  {
    rushes: [
      [K.Bucket, K.Wisp],
      [K.Crow, K.Moth, K.Spitter, K.Deadeye],
      [K.Runner, K.Brute],
      [K.Digger, K.Hearse],
    ],
  },
  {
    rushes: [
      [K.Wisp, K.Moth, K.Lobber, K.Deadeye],
      [K.Bucket, K.Bearer, K.Crow, K.Brute],
      [K.Runner, K.Digger, K.Hearse],
      [K.Spitter, K.Wisp, K.Bloater, K.Bucket],
    ],
  },
];

/** Night `n`'s place in its long act (`ACT_NIGHTS`): for nights past the finale (endless ones: the last's). */
export const actNight = (n: number): ActNight =>
  ACT_NIGHTS[(Math.max(FINALE + 1, Math.min(n, LONG.last)) - FINALE - 1) % ACT] as ActNight;

/** The kinds night `n` rushes (`ACTS`): none on the script's nights, the endless ones and a long act's others. */
export const rushOn = (n: number): readonly ZombieKind[] => {
  const slot = n > FINALE && n <= LONG.last ? actNight(n).rush : undefined;
  return slot === undefined ? [] : (ACTS[actOf(n) - 2]?.rushes[slot] ?? []);
};

/**
 * A Spitter stops and spits acid (ZOMBIE_ACID) at what is within `range` px: farther than a Sprinkler, a Beehive or a
 * Sunflower reaches (`towerReach`, a Bright Petals' 210 too; only a Mortar outreaches it), so a wall of towers can't
 * answer it — a gardener or a Mortar has to.
 */
export const SPITTER = { range: 215 } as const;

/**
 * A Lobber stops within `range` px of a gardener (them first), else a build or the Greenhouse — no sight needed — and
 * lobs a rotten pumpkin at where it stands (content ROTTEN_GOURD): it flies over everything for its flight, its landing
 * marked on the ground, and bursts for the Lobber's damage there. Fences and hedges are no cover from it.
 */
export const LOBBER = { range: 200 } as const;

/**
 * A Deadeye stops within `range` px of a gardener in sight (them first), else a tower, and aims: its line starts on its
 * mark and turns after it at most `turn` radians a second for `aimTicks`, then one shot down the line to `range` (a wall
 * stops it): every gardener, build and the Greenhouse within `width` px of it takes the Deadeye's damage (builds its
 * `vsBuild`). Step off the line, or behind a wall.
 */
export const DEADEYE = { range: 200, aimTicks: ticks(1.6), turn: 0.35, width: 2 } as const;

/**
 * A Bloater bursts `fuseTicks` after touching a build, a gardener or the Greenhouse (within `touch` px), or at once when
 * killed: `damage` (scaled by the night) to everything within `radius` px — zombies too — its `vsBuild` times that to
 * builds and the Greenhouse.
 */
export const BLOATER = { radius: 42, damage: 70, fuseTicks: ticks(0.6), touch: 4 } as const;

/**
 * A Digger tunnels at `speed` px/s straight for the Greenhouse, out of reach (only bees find it underground), and
 * surfaces `surfaceTicks` after coming within `near` px of the Greenhouse, undermining it as it bursts up (`undermine` of
 * the Greenhouse's health, before anyone can shoot it; a party's split by its `partyCount`: its nights bring that many
 * times the Diggers, and guns don't reach them); then it walks and fights like a Shambler. Builds don't bring it up
 * (ADR-0184): wet ground does. Tunnelling into a Sprinkler's spray (within its reach) it is forced up there and then,
 * dazed for `wetTicks` among the towers that soaked it: the Digger's question, the Sprinkler its answer. `builds` 1 (a
 * balance experiment): it comes up by the first build it nears too, as before, undermining nothing.
 */
export const DIGGER = {
  speed: 34,
  near: 26,
  surfaceTicks: ticks(0.7),
  wetTicks: ticks(1.6),
  undermine: 0.012,
  builds: 0,
};

/**
 * A Moth (ADR-0184) flies to a Sunflower, the nearest that fewer than `perFlower` moths are on or after, else the
 * nearest; with none standing, to the Greenhouse's lit windows. Landed on a Sunflower it smothers it: the flower fires
 * nothing while a moth sits on it, and the moth chews it (its `damage` × `vsBuild` a blow). No tower but a Beehive
 * reaches a flyer, so a wall of Sunflowers needs hives among it, or gardeners who swat.
 */
export const ZOMBIE_MOTH = { perFlower: 2 } as const;

/**
 * The Harvest Horror: every `slamEveryTicks` it rears up for `windupTicks` and slams the ground `reach` px ahead,
 * `damage` (× the night) to everything within `radius` of it (its `vsBuild` times that to builds), and every
 * `summonEveryTicks` it calls `summon` Shamblers up round it.
 */
export const HORROR = {
  slamEveryTicks: ticks(4.5),
  windupTicks: ticks(1),
  reach: 22,
  radius: 50,
  damage: 40,
  summonEveryTicks: ticks(14),
  summon: 3,
} as const;

/**
 * The Thresher drives at the builds near it (`SIEGE`), else down its lane to the Greenhouse straight through whatever
 * stands in it (its field counts no build): every `ZombieDef.attackTicks` its blades mow what is within `blade` px ahead
 * of its body and `width` px either side of its line — its `damage` (× the night; builds its `vsBuild` times that, the
 * Greenhouse `vsBase` times), gardeners thrown aside (`knock`). Its front is armoured (`ZombieDef.guard`), its engine at the back takes `guard.back` times.
 */
export const THRESHER = { blade: 10, width: 13, vsBase: 0.4, knock: 160 } as const;

/**
 * The Hearse (ADR-0168): it drives down its lane to the Greenhouse straight through whatever stands in it (the Thresher's
 * field), never after anyone; every `ZombieDef.attackTicks` it rams what is within `blade` px ahead of its body and
 * `width` either side of its line (its `damage` × the night; builds its `vsBuild`; gardeners thrown aside, `knock`). Its
 * steel stops a shot at its front or its back (`ZombieGuard.rear`): only its wheels, from the side, or what bites from
 * underfoot hurt it. Its wheels gone it stops where it stands, and its `cargo` climbs out there; at the Greenhouse it
 * crashes into the door (`crash` times its ram) and its cargo climbs out there. Where it is stopped is the question.
 */
export const HEARSE = {
  blade: 6,
  width: 11,
  knock: 150,
  crash: 8,
  cargo: [
    ZombieKind.Shambler,
    ZombieKind.Runner,
    ZombieKind.Shambler,
    ZombieKind.Runner,
    ZombieKind.Shambler,
    ZombieKind.Brute,
  ],
  /** They climb out within this many px of where it stopped. */
  spread: 14,
} as const;

/**
 * The Gravedigger walks her lane like the dead, and every `everyTicks` with a gardener, a build or the Greenhouse within
 * `range` px she heaves her shovel at it: `windupTicks` of warning (the cone shows on the ground), then what stands within
 * `range` px and `arc` radians either side of where she swung takes `damage` (× the night; builds her `vsBuild` share),
 * gardeners thrown back (`knock` px/s).
 */
export const GRAVEDIGGER = {
  everyTicks: ticks(2.5),
  windupTicks: ticks(0.5),
  range: 72,
  arc: 0.9,
  damage: 45,
  knock: 170,
} as const;

/**
 * The Moon Priest walks its lane like the dead; climbing out, it plants `altars` Moon Altars (`ALTAR`) in one patch near
 * the Greenhouse, one trip to break them all: on ground the dead can walk to, `near` to `far` px from the Greenhouse, the
 * first anywhere there, the rest within `spread` px of it and at least `apart` px from each other (the run's hidden draw,
 * `tries` a spot). While one of them stands nothing hurts it — not even what bites from underfoot.
 */
export const PRIEST = { altars: 3, near: 160, far: 320, spread: 150, apart: 70, tries: 80 } as const;

/**
 * A Moon Altar fires a ring of `bolts` (content ALTAR_BOLT; its own damage, × the night) every `ZombieDef.attackTicks`,
 * each ring turned half a gap on from the last, so standing still between two bolts works once.
 */
export const ALTAR = { bolts: 8 } as const;

// ─── The weekly deck's variants (ADR-0226) ─────────────────────────────────────────────────────────────────

/**
 * A Mossback (ADR-0226): `regrowTicks` after the last blow that hurt it its moss grows it back whole, unless fire or
 * poison is on it (a burn), which stops the moss while it lasts. Chip damage spread along a lane does nothing; a defence
 * that kills it in one go, or sets it alight, does.
 */
export const MOSS = { regrowTicks: ticks(2) } as const;

/**
 * The Charred (ADR-0226): nothing a beam or fire does reaches it (a Sunflower's beam and the Laser, flames, a fire on it
 * or under it: a Jack-o'-Fire's, Firethorn's, Fire Rounds'), and a Sprinkler's water does `water` times its damage.
 * Hell's commonest variant (`ZombieDef.hell`).
 */
export const CHARRED = { water: 3 } as const;

/**
 * The Beekeeper (ADR-0226): every bee within `smoke` px of him falls asleep in his smoke, spent (a hive's and a Hive
 * Launcher's alike), and his veil stops a sting on him whole. He walks among a crowd (`ZombieDef.escort`): the hives go
 * quiet round it until he is down.
 */
export const KEEPER = { smoke: 48 } as const;

/**
 * The Parasol (ADR-0226): the dead within `cover` px of her (her too) take nothing from what falls on them from above
 * (a Mortar's pumpkins, bees' stings: a hive's, a Hive Launcher's), nor what those set off (a Heavy Shell's crack, a
 * Giant Pumpkin's stun, a sting's poison or honey). A beam comes from the side, and so does a gun.
 */
export const PARASOL = { cover: 30 } as const;

/**
 * The Bone Balloon (ADR-0226): a flyer whose body is its gasbag, `height` px north of its
 * shadow (shot up there, as a battle royale's balloon is, ADR-0199), floating at its pace straight for the Greenhouse
 * over everything. Popped, its `cargo` drops where its shadow is, `spread` px round it; over the Greenhouse it comes down
 * on it (`crash` times its blow) and its cargo climbs out at the door. A set piece (`nightBalloons`): `debut` of them on a
 * night the deck deals it, `after` a night from then on (a party's `partyCount` times as many).
 */
export const BONE_BALLOON = {
  height: 60,
  cargo: [ZombieKind.Bucket, ZombieKind.Bucket, ZombieKind.Bucket, ZombieKind.Bucket],
  spread: 12,
  crash: 6,
  debut: 3,
  after: 1,
} as const;

/**
 * The Grave Robber (ADR-0226): it rises at a gate and runs a route of its own round and round, `stops` spots of the yard
 * `near` to `far` px from the Greenhouse (a hidden draw, `world.secret`, `tries` a spot), hurting nobody; `ticks` after it
 * rose it digs out where it stands, gold and all. Killed, its sack pays its bounty (a prize: no night shrinks it). One on a
 * night the deck deals it, no more: a rare night.
 */
export const ROBBER = { ticks: ticks(20), stops: 4, near: 110, far: 300, tries: 60 } as const;

/**
 * The Sapper (ADR-0226): he walks down his lane as the siege kinds do; at the first build in his way (or the Greenhouse)
 * he kneels and lights a keg against it, and `fuseTicks` later it goes up: every build within `radius` px of it gone, the
 * Greenhouse `house` of its health, every gardener in it hurt `damage` (× the night) and thrown (`knock` px/s), and him
 * with it. Killed first, it fizzles.
 */
export const SAPPER = { fuseTicks: ticks(5), radius: 22, house: 0.08, damage: 40, knock: 220 } as const;

/**
 * The Fence Thief (ADR-0226): down its lane as the siege kinds; at the first fence in its way it pulls for `pullTicks`
 * and carries it off (the fence gone for good) back to its gate at `carry` px/s, where it is gone. A Thorns fence it can't
 * grip: it strikes that one as any dead does, and the thorns prick it.
 */
export const THIEF = { pullTicks: ticks(1.4), carry: 18 } as const;

/**
 * The weekly deck (ADR-0226): how the variants come. Nights 1-20 never change (a stranger meets the run they meet
 * today); from night `from` a slot every `every` nights deals a variant its first night, said at the dawn before as every
 * new kind is; none on the second stage's first night (`NIGHTS.mazeFrom`: Chomper's fire and the maze are news enough),
 * the next slot two nights on. Each farm is dealt its own each week (`deckOf`: the weekly boards' week, ADR-0185): the
 * maze breakers (`breaker`) only from the second stage, the farm's `tilt` dealing the hard questions (`hard`: 1, a tower
 * you own answers it, to 3, it breaks a defence) first on the easy farm and last on the hard one, `lean` a step of `hard`.
 * Once every variant has come, a slot deals two at once: a question no night has asked. A slot's variants come
 * `NIGHTS.spotlight` times as often that night; from their first they are in every night's mix at their weight.
 */
export const DECK: {
  readonly from: number;
  readonly every: number;
  readonly hard: Readonly<Partial<Record<number, number>>>;
  readonly breaker: readonly ZombieKind[];
  readonly tilt: Readonly<Record<string, number>>;
  readonly lean: number;
  readonly salt: number;
} = {
  from: 21,
  every: 4,
  hard: {
    [ZombieKind.Charred]: 1,
    [ZombieKind.Beekeeper]: 1,
    [ZombieKind.Robber]: 1,
    [ZombieKind.Mossback]: 2,
    [ZombieKind.Parasol]: 2,
    [ZombieKind.Balloon]: 3,
    [ZombieKind.Sapper]: 3,
    [ZombieKind.Thief]: 3,
  },
  breaker: [ZombieKind.Sapper, ZombieKind.Thief],
  tilt: { 'willow-bend': 1, 'twin-hollows': -1 },
  lean: 0.35,
  salt: 0x6465636b,
};

/**
 * A run's deck (ADR-0226): its slots in order (each a night and the variants it deals), each variant's first night, and
 * what it was dealt from: a week's (`deckOf`) or a dev knob's variants (`deckFrom`), as the wire sends it.
 */
export interface Deck {
  readonly slots: readonly { readonly night: number; readonly kinds: readonly ZombieKind[] }[];
  readonly debut: Readonly<Partial<Record<number, number>>>;
  readonly week?: number;
  readonly kinds?: readonly ZombieKind[];
}

/** The deck's slot nights (`DECK`): every `every` from `from`, none on the second stage's first, up to the run's last. */
export function deckNights(): number[] {
  const out: number[] = [];
  for (let n = DECK.from; n < NIGHTS.mazeFrom; n += DECK.every) out.push(n);
  for (let n = NIGHTS.mazeFrom + 2; n <= LONG.last; n += DECK.every) out.push(n);
  return out;
}

const DECKS = new Map<string, Deck>();

/**
 * Week `week`'s deck for map `map` (ADR-0226, `DECK`; a week as `weekOf` counts it): the variants in an order the week
 * draws (the same for every run of that farm that week), the farm's tilt leaning it, each dealt the first slot it may come
 * on; then a pair a slot. Pure: the server, every client and the harness deal the same.
 */
export function deckOf(map: string, week: number): Deck {
  const key = `${map}:${week}`;
  const known = DECKS.get(key);
  if (known) return known;
  const salt = hash32(week, ZOMBIE_MAPS.indexOf(map) + 1, DECK.salt);
  const tilt = DECK.tilt[map] ?? 0;
  const variants = ZOMBIES.filter((z) => z.variant).map((z) => z.kind);
  const score = (k: ZombieKind) => hash32(salt, k) / 0x100000000 + tilt * ((DECK.hard[k] ?? 2) - 2) * DECK.lean;
  const left = [...variants].sort((a, b) => score(b) - score(a) || a - b);
  const nights = deckNights();
  const slots: { night: number; kinds: ZombieKind[] }[] = [];
  let i = 0;
  for (; i < nights.length && left.length > 0; i++) {
    const night = nights[i] as number;
    const at = left.findIndex((k) => walksRound(night) || !DECK.breaker.includes(k));
    if (at >= 0) slots.push({ night, kinds: left.splice(at, 1) });
  }
  const pairs = variants
    .flatMap((a, j) => variants.slice(j + 1).map((b) => [a, b] as [ZombieKind, ZombieKind]))
    .sort((p, q) => hash32(salt, p[0], p[1]) - hash32(salt, q[0], q[1]));
  for (let p = 0; i < nights.length; i++, p++)
    slots.push({ night: nights[i] as number, kinds: pairs[p % pairs.length] as ZombieKind[] });
  const debut: Partial<Record<number, number>> = {};
  for (const s of slots) for (const k of s.kinds) debut[k] ??= s.night;
  const deck = { slots, debut, week };
  DECKS.set(key, deck);
  return deck;
}

/** A deck that deals `kinds` every night (a dev knob's, `?variant=`: to playtest them on any night of a jump). */
export function deckFrom(kinds: readonly ZombieKind[]): Deck {
  const key = `dev:${kinds.join(',')}`;
  const known = DECKS.get(key);
  if (known) return known;
  const slots = Array.from({ length: LONG.last }, (_, i) => ({ night: i + 1, kinds }));
  const deck = { slots, debut: Object.fromEntries(kinds.map((k) => [k, 1])), kinds };
  DECKS.set(key, deck);
  return deck;
}

/** The variants named `keys` (`mossback,charred`: their `ZombieDef.key`s, a dev knob's), the rest left out. */
export const variantsNamed = (keys: string): ZombieKind[] =>
  keys.split(',').flatMap((k) => ZOMBIES.filter((z) => z.variant && z.key === k.trim()).map((z) => z.kind));

/** The deck the wire says a run was dealt (`MatchInfo.zombies`' `week` and `variants`, ADR-0226) on `map`, or none. */
export const deckSent = (map: string, week: number, variants: readonly number[]): Deck | null =>
  variants.length > 0 ? deckFrom(variants as ZombieKind[]) : week > 0 ? deckOf(map, week) : null;

/** The night kind `def` first comes in a run dealt `deck` (its own `from`; a variant's, the deck's; none without one). */
export const firstNight = (def: ZombieDef, deck?: Deck | null): number =>
  def.variant ? (deck?.debut[def.kind] ?? DEALT) : def.from;

/** The variants `deck` deals night `n` (its slot's), or none. */
export const dealtOn = (n: number, deck?: Deck | null): readonly ZombieKind[] =>
  deck?.slots.find((s) => s.night === n)?.kinds ?? [];

/** How many Bone Balloons night `n` brings a party of `players` (`BONE_BALLOON`: a set piece). */
export function nightBalloons(n: number, players: number, deck?: Deck | null): number {
  const from = firstNight(zombieDef(ZombieKind.Balloon), deck);
  if (n < from) return 0;
  const each = dealtOn(n, deck).includes(ZombieKind.Balloon) ? BONE_BALLOON.debut : BONE_BALLOON.after;
  return Math.round(each * partyCount(players));
}

/** How many Grave Robbers night `n` brings (`ROBBER`: one on a night the deck deals it, a party's too). */
export const nightRobbers = (n: number, deck?: Deck | null): number =>
  dealtOn(n, deck).includes(ZombieKind.Robber) ? 1 : 0;

/**
 * The Blight walks the way your towers cover least (its field costs a cell `cover` (1/10 cells) more for each tower that
 * reaches it: round them over open grass, in through the gap nobody watches) until it is within `root` px of the
 * Greenhouse or a build is in its way, then takes root there for good (`rootTicks`) and spreads rot round itself: `start` px across
 * once rooted, `grow` px a second more, at most `max`. Every `everyTicks` the rot eats what stands in it — builds and the
 * Greenhouse `rot` (× the night, builds its `vsBuild`), gardeners `rotPlayer` — from underfoot (nothing held up stops it).
 * Killed, the rot goes with it.
 */
export const BLIGHT = {
  cover: 40,
  root: 300,
  rootTicks: ticks(1.5),
  start: 24,
  grow: 6,
  max: 230,
  everyTicks: ticks(1),
  rot: 12,
  rotPlayer: 6,
} as const;

/** The rot's reach round a Blight rooted `ticks` ago (px). */
export const blightRadius = (rooted: number): number =>
  Math.min(BLIGHT.max, BLIGHT.start + (BLIGHT.grow * rooted) / ticks(1));

/**
 * The Buried King (ADR-0164): the run's first mega boss. The last of night `night`'s dead down, the ground gives way: `introTicks`
 * later the party stands in the Barrow (the map's first arena), everyone up and whole, and he rises over `riseTicks`. Nobody
 * gets up again down there: with the last gardener the run is lost. He down, `outroTicks` later the party is back at the
 * Greenhouse and the night's dawn comes.
 *
 * - **His fists** (`ZombieKind.Fist`, two; `slam.fist`): each on its own, `gapTicks` up out of sight, then its shadow
 *   follows a gardener at `speed` px/s for `stalkTicks`, stops for `fallTicks` (never hurried: the time to step out of it
 *   from a standstill, and a moment to see it) and the fist comes down: `damage` to every
 *   gardener within `radius` px of it, thrown back (`knock`). It lies there `restTicks` — the only time it can be hit —
 *   and goes up again. The second starts half a turn after the first. With one broken the other hurries (`hurry` of
 *   every wait).
 * - **His head** (`ZombieKind.King`), once both fists are broken and he has roared (`roarTicks`): two attacks, then the
 *   head itself comes down like a fist (`slam.head`) and lies there `restTicks`, the only time it can be hit; and again,
 *   every round `tempo` of the last one's waits (never under `tempoFloor`).
 *   - **Grave rain** (`rain`): `volleys` volleys `gapTicks` apart, each a tombstone on every gardener and `extra` more
 *     about the floor (`spread` px apart at least); each lands `warnTicks` after its mark shows, `damage` within `radius`.
 *   - **Cracks** (`cracks`): the floor in strips `width` px wide from wall to wall. Every other strip bursts `warnTicks`
 *     after its mark shows, and as it does the rest are marked (stand between them, then step across); or, every other
 *     time, all of them at once `combTicks` after their marks show but one strip in `every` (a safe one is never more
 *     than two strips off: run for it). `damage`.
 * - **The dead** (`adds`) climb onto the platform all the while and go for the gardeners: nothing heals down there.
 * - What a client shows of him is ~100 ms old (snapshots are drawn late), so a blow lands `graceTicks` after its warning
 *   ran out: who is out on their own screen is out.
 * - Health: his parts' `ZombieDef.maxHp`, `partyHp` more a gardener past the first (`kingHp`); the party's damage on
 *   them is the night's (`defenceScale`).
 */
export const KING = {
  night: 10,
  introTicks: ticks(2.6),
  /**
   * All the intro long every gardener is held where they stand (dazed): the ground opens under them and they fall. In
   * the Barrow they come down out of the sky for `landTicks`, held the same.
   */
  landTicks: ticks(1.35),
  riseTicks: ticks(4),
  roarTicks: ticks(2.6),
  outroTicks: ticks(5.5),
  graceTicks: 3,
  partyHp: 0.85,
  slam: {
    fist: {
      gapTicks: ticks(0.8),
      stalkTicks: ticks(1.1),
      fallTicks: ticks(0.7),
      restTicks: ticks(3.2),
      speed: 110,
      radius: 24,
      knock: 200,
      hurry: 0.65,
    },
    head: {
      gapTicks: ticks(0.9),
      stalkTicks: ticks(1.2),
      fallTicks: ticks(1),
      restTicks: ticks(5),
      speed: 80,
      radius: 46,
      knock: 230,
      hurry: 1,
    },
  },
  rain: { volleys: 3, gapTicks: ticks(0.95), extra: 4, spread: 40, warnTicks: ticks(1.15), radius: 20, damage: 22 },
  cracks: { width: 32, warnTicks: ticks(1.2), combTicks: ticks(1.6), every: 3, damage: 24 },
  /** His head roars `cueTicks` before each attack, and waits `pauseTicks` after its last blow landed. */
  cueTicks: ticks(0.5),
  pauseTicks: ticks(0.8),
  tempo: 0.92,
  tempoFloor: 0.75,
  /**
   * His arms, a man's two: each from its `shoulder` (px from where his head hangs, `kingArena`'s `head`; his right one,
   * on our left, mirrored) to its fist. A fist lying on the floor has its arm lying there too, straight from the
   * shoulder to the fist's `wrist` (px from where the fist is), and nobody walks through either (`kingLimb`): the fist a
   * circle `fist` px across, the arm one `radius` px every `step`. Neither comes down nearer the floor's edge than leaves
   * `room` px to pass, and a gardener one comes down on (`sunk` px or more in it) is put beside it.
   */
  arm: { shoulder: [82, -92], wrist: -12, step: 12, radius: 11, fist: 15, room: 12, sunk: 2 },
  /**
   * His head lying on the floor, as a gardener bumps into it: circles (x, y from where it is, radius). It never lands
   * nearer the floor's south edge than `chin` px: its jaw, as it is drawn, stays on the floor.
   */
  skull: [
    [0, 0, 40],
    [0, -38, 32],
  ],
  chin: 55,
  /**
   * The dead that climb onto the platform while he stands: the first pack `firstTicks` after his fists first rise, then
   * one every `everyTicks`; `pack` of them (`kinds` in turn) and `perPlayer` more for each gardener past the first,
   * never more than `alive` (+ `alivePerPlayer`) standing. They rise `rim` px in from its west, east and south edges,
   * `gap` px from any gardener at least, and go straight for the nearest one. No gold for them (he would be farmed);
   * his own blows crush them too, and they fall with him.
   */
  adds: {
    firstTicks: ticks(6),
    everyTicks: ticks(10),
    pack: 3,
    perPlayer: 1,
    alive: 5,
    alivePerPlayer: 2,
    kinds: [ZombieKind.Shambler, ZombieKind.Runner, ZombieKind.Shambler],
    rim: 10,
    gap: 48,
  },
} as const;

/**
 * How many times the health each part of a mega boss (the Buried King, the Yoke) has against a party of `players`, met
 * after night `night` (a long act's rematch: its `MEGA.hp`).
 */
export const kingHp = (players: number, night = 0): number =>
  (1 + KING.partyHp * Math.max(0, players - 1)) * (megaAt(night)?.hp ?? 1);

/**
 * A run from a later night's purse (dev `?night=`, the arena's `--from`; players always start on night 1, ADR-0228): what
 * the harness's `plaza` defenders hold at that night's dusk, gold and builds at cost (`worth`, `tools/arena zombies
 * --worth`), and `cards` of it more for each dawn's card it doesn't have (a card does more the later it comes).
 */
export const PURSE = {
  /**
   * A gardener's worth at night n's dusk alone (n from 1); past the table `LONG.worth` more a night, past the run's last
   * `NIGHTS.endlessGrowth` times. Measured on Moonlit Farm once ADR-0227's gold and towers were in (72 seeds, the median
   * of the runs that got there: night 15's dips as night 14's Gravedigger and hearses break towers).
   */
  worth: [
    300, 525, 750, 1050, 1350, 1850, 2250, 2750, 3300, 3950, 5000, 5750, 6400, 7150, 7000, 7650, 8100, 9750, 10850,
    11900,
  ],
  cards: 0.045,
} as const;

/** A run from night `from`'s purse, alone (`startGold` shares it out): `NIGHTS.startGold` from night 1. */
export const runPurse = (from: number): number => {
  if (from <= 1) return NIGHTS.startGold;
  const w = PURSE.worth;
  const past = Math.max(0, Math.min(from, LONG.last) - w.length);
  const worth =
    ((w[Math.min(from, w.length) - 1] as number) + LONG.worth * past) * pow(NIGHTS.endlessGrowth, endless(from));
  // ponytail: the cards' share stops at the finale's; the long acts' purse grows at the second act's measured rate, the
  // later acts' a guess until `--worth` measures them (`--max 110`).
  return Math.round((worth * (1 + PURSE.cards * (Math.min(from, FINALE) - 1))) / 50) * 50;
};

/** The night a run asked to start on `night` starts on (a whole night, 1 at least). */
export const runFrom = (night: number | undefined): number =>
  night !== undefined && Number.isInteger(night) && night > 1 ? night : 1;

/**
 * The Yoke (ADR-0165): the Buried King's plough team, the run's second mega boss. The last of night `night`'s dead down,
 * the ground gives way as it did for him (`KING.introTicks`, `landTicks`, `outroTicks`, `graceTicks` are both fights')
 * and the party stands in the Fallow (the map's second arena), everyone up and whole; the team climbs out of the field
 * over `riseTicks`. Nobody gets up again down there. An ox is armoured in front (`ZombieDef.guard`: a shot at its face
 * is stopped, one at its back counts `back` times) and turns after its mark faster than a gardener gets round it: it
 * is hit from behind once it has run past, or by a mate while it faces another.
 *
 * - **Yoked** (`ZombieKind.Yoke`, `team`: the two `span` px either side of the beam's middle, one body): it ploughs the
 *   field from end to end, east and west. It turns to the far end and comes level with its mark (`ZombieDef.speed`) for
 *   `paceTicks`, then drops its horns: its row slides after the mark for `trackTicks` (at most `slide` px a second)
 *   and holds for `lockTicks` (never hurried: the time to step out of it from a standstill, and a moment to see it);
 *   then it runs the row at `charge` px/s, everyone within `lane` px of its beam run down (its `damage`, thrown aside:
 *   `knock`), to the floor's far edge (`YOKE.edge` px short of it), where it stands winded for `windedTicks`, its back
 *   to the field. Its health gone, the beam snaps: the two are loose, dazed for `YOKE.snapTicks`.
 * - **Loose** (`ZombieKind.Ox`, two; `ox`): each on its own mark (another gardener's than the other's while there is
 *   one). It walks round its mark at `reach` px for `paceTicks`, drops its horns — its line turns after the mark for
 *   `trackTicks` (at most `aim` radians a second), holds for `lockTicks` — and runs down it as the pair did its row.
 *   Both drop their horns at the same moment (one that is ready walks on until the other is). The first keeps to the
 *   east or the west of its mark, the second goes round (at `trot` px/s) to the far side of its own mark from the
 *   first, so the two come at a gardener from both sides: who stands between them as the lines hold, and steps out, has them meet. One that runs
 *   into the other (within `crash` px of its line, or into the one that is down) stops there: both are dazed for
 *   `crashTicks`, their guard down (every hit counts `guard.back` times, from anywhere). One that meets nothing stops at
 *   the edge for `windedTicks`. They learn: after each run the far side is `skew` radians more off (a hidden draw
 *   either way, at most `skewMax`), so a gardener has to bring the lines together with their own feet while they
 *   follow them. Every run `tempo` of the last one's waits (never under `tempoFloor`).
 * - **One down** (`ZombieKind.Carcass`): it lies where it fell, in the way; the other hurries (`hurry` of every wait)
 *   and has nothing to run into but it.
 * - **Furrows** (`furrow`): they are plough oxen still. Every run tears a furrow from where it began to where it ended
 *   (the pair's two), and it burns for `ticks`: a gardener within `width` px of its line takes `damage` every
 *   `everyTicks`, from underfoot. Narrower than a lane: who stepped out of the run is out of its furrow; but the field
 *   fills with them, and where the two met last is no place to stand.
 * - Horns: a gardener right against an ox that is walking is tossed (`toss`), so nobody stands in one.
 * - What stands still is in a gardener's way (`kingLimb`: an ox winded or dazed, the one that is down), `body` px round.
 * - **The dead** (`adds`) climb onto the field all the while, no tougher than the Barrow's were; an ox runs them down too.
 */
export const YOKE = {
  night: 20,
  riseTicks: ticks(3.4),
  snapTicks: ticks(2.4),
  team: {
    span: 30,
    paceTicks: ticks(2),
    trackTicks: ticks(0.6),
    lockTicks: ticks(1.1),
    slide: 70,
    charge: 230,
    lane: 48,
    knock: 250,
    windedTicks: ticks(3.2),
  },
  ox: {
    reach: 140,
    trot: 110,
    paceTicks: ticks(2.2),
    trackTicks: ticks(0.55),
    lockTicks: ticks(0.85),
    aim: 2.2,
    charge: 260,
    lane: 19,
    knock: 220,
    windedTicks: ticks(1.5),
    crash: 26,
    crashTicks: ticks(3.6),
    hurry: 0.7,
  },
  skew: 0.2,
  skewMax: 0.7,
  tempo: 0.94,
  tempoFloor: 0.75,
  body: 19,
  edge: 28,
  toss: { reach: 3, damage: 14, knock: 170, everyTicks: ticks(0.9) },
  furrow: { width: 10, ticks: ticks(9), everyTicks: ticks(0.5), damage: 10 },
  /**
   * As `KING.adds`, but the dead that climb on are night `night`'s (the King's own night's), not the Yoke's: at night
   * 15's health they, not the oxen, were what felled a gardener alone (a third of the harness's fights).
   */
  adds: {
    firstTicks: ticks(7),
    everyTicks: ticks(11),
    pack: 3,
    perPlayer: 1,
    alive: 5,
    alivePerPlayer: 2,
    kinds: [ZombieKind.Runner, ZombieKind.Shambler, ZombieKind.Runner],
    rim: 10,
    gap: 48,
    night: 10,
  },
} as const;

/**
 * The run's mega bosses (ADR-0164, ADR-0165), in the order a run meets them: the night whose dawn waits for each, the
 * arena (`MapDef.arenas`' index) it is fought in, and its lines: its `name` over its health, what the fallen read while
 * it stands (`wait`), what a run lost to it is called (`lost`). Each long act (ADR-0179) ends on one of them again, its
 * parts' health `hp` times the first meeting's (`kingHp`): the party's guns and cards have grown since.
 */
const THE_KING = {
  key: 'king',
  arena: 0,
  name: 'THE BURIED KING',
  wait: 'DOWN UNTIL THE KING FALLS',
  lost: 'THE KING BURIED YOU',
} as const;
const THE_YOKE = {
  key: 'yoke',
  arena: 1,
  name: 'THE YOKE',
  wait: 'DOWN UNTIL THE YOKE FALLS',
  lost: 'THE YOKE BROKE YOU',
} as const;
export const MEGA = [
  { ...THE_KING, night: KING.night, hp: 1 },
  { ...THE_YOKE, night: YOKE.night, hp: 1 },
  { ...THE_KING, night: 40, hp: 4 },
  { ...THE_YOKE, night: 60, hp: 5 },
  { ...THE_KING, night: 80, hp: 8 },
  { ...THE_YOKE, night: 100, hp: 10 },
] as const;
export type MegaBoss = (typeof MEGA)[number];

/** Is zombie kind `kind` of the Yoke's (ADR-0165: the pair under its beam, an ox, the one that is down)? */
export const isYoke = (kind: number): boolean =>
  kind === ZombieKind.Yoke || kind === ZombieKind.Ox || kind === ZombieKind.Carcass;

/** The mega boss night `night`'s dawn waits for, or null. */
export const megaAt = (night: number): MegaBoss | null => MEGA.find((m) => m.night === night) ?? null;

/**
 * What a gardener bumps into of a part of a mega boss that lies or stands still on the floor: `kind`, where it faces
 * and where it is. The Buried King's (ADR-0164): his head's skull; a fist (facing: its side, 0 his right one, on our
 * left) and the arm it is the end of, from its wrist straight back to the shoulder, as far as that is over the floor.
 * The Yoke's (ADR-0165): an ox, the two under their beam, the one that is down. The sim stands these there (`Limb`s)
 * and the client's predictor the same from what it is told of the part, so both collide alike.
 */
export function kingLimb(
  map: Pick<MapDef, 'arenas'>,
  kind: number,
  facing: number,
  x: number,
  y: number,
): { x: number; y: number; r: number }[] {
  // Where the wire puts it (a sixteenth of a px, a 256th of a turn): the same on the server and on a client.
  x = Math.round(x * 16) / 16;
  y = Math.round(y * 16) / 16;
  facing = ((Math.round((facing / (Math.PI * 2)) * 256) & 0xff) / 256) * Math.PI * 2;
  if (kind === ZombieKind.Ox || kind === ZombieKind.Carcass) return [{ x, y, r: YOKE.body }];
  if (kind === ZombieKind.Yoke) {
    const [px, py] = [-sin(facing) * YOKE.team.span, cos(facing) * YOKE.team.span];
    return [
      { x: x - px, y: y - py, r: YOKE.body },
      { x, y, r: YOKE.body / 2 },
      { x: x + px, y: y + py, r: YOKE.body },
    ];
  }
  const arena = kingArena(map);
  if (!arena) return [];
  if (kind === ZombieKind.King) return KING.skull.map(([dx, dy, r]) => ({ x: x + dx, y: y + dy, r }));
  const a = KING.arm;
  const side = cos(facing) < 0 ? 1 : -1;
  const out: { x: number; y: number; r: number }[] = [{ x, y, r: a.fist }];
  const [wx, wy] = [x, y + a.wrist];
  const [dx, dy] = [arena.head.x + side * a.shoulder[0] - wx, arena.head.y + a.shoulder[1] - wy];
  const d = hypot(dx, dy);
  for (let at = a.step; at < d; at += a.step) {
    const [px, py] = [wx + (dx / d) * at, wy + (dy / d) * at];
    // Past the platform's north edge it hangs over the sea.
    if (py < arena.y0) break;
    out.push({ x: px, y: py, r: a.radius });
  }
  return out;
}

/** Where a mega boss's fight is (`MatchInfo.zombies.king`): not on, the ground giving way, the fight, its end. */
export const KingStage = { None: 0, Intro: 1, Fight: 2, Outro: 3 } as const;
export type KingStage = (typeof KingStage)[keyof typeof KingStage];

/** A mega boss's arena as its fight uses it (`megaArena`). */
export interface MegaArena {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  head: { x: number; y: number };
  spawn: (seat: number, players: number) => { x: number; y: number };
}

/**
 * The map's arena `index` as a mega boss's fight uses it, in world px: its floor's box, the middle of the floor's north
 * edge (`head`: where the King's hangs between attacks, where the Yoke climbs out), where the party of `players` stands
 * as it arrives (a row along the south).
 */
export function megaArena(map: Pick<MapDef, 'arenas'>, index: number): MegaArena | null {
  const f = map.arenas[index]?.floor;
  if (!f) return null;
  const [x0, y0, x1, y1] = [f.cx * TILE_SIZE, f.cy * TILE_SIZE, (f.cx + f.w) * TILE_SIZE, (f.cy + f.h) * TILE_SIZE];
  const mid = (x0 + x1) / 2;
  return {
    x0,
    y0,
    x1,
    y1,
    head: { x: mid, y: y0 + 2 },
    spawn: (seat, players) => ({ x: mid + (seat - (Math.max(1, players) - 1) / 2) * 28, y: y1 - 28 }),
  };
}

/** The Barrow as the King's fight uses it (ADR-0164). */
export const kingArena = (map: Pick<MapDef, 'arenas'>): MegaArena | null => megaArena(map, MEGA[0].arena);

/** The Fallow as the Yoke's fight uses it (ADR-0165). */
export const yokeArena = (map: Pick<MapDef, 'arenas'>): MegaArena | null => megaArena(map, MEGA[1].arena);

/**
 * The dead keep to their lanes (the dirt paths from the gates and the Greenhouse's cobbles): their field counts a step onto
 * any other ground as `offLane` 1/10 cells more — far more than a wall of builds across a lane costs (`BuildDef.pathCost`),
 * so a fence in their lane is broken, never walked round, and one off the lanes never meets them. One off its lane (risen
 * beside it, thrown, after a gardener) walks the shortest way back onto it.
 */
export const LANES = { offLane: 1000 } as const;

/**
 * Chomper (ADR-0180, ADR-0186): a demon of a combine harvester that ends the run's first stage. On the day after
 * night `night` it rolls in along its map's way (`MapDef.chomper`: careening round the whole yard as if it had lost its
 * mind) once `wait` is up (its warning), as its scream goes (its `gait`, the recording's own timeline, `cycle` ticks long,
 * twice over: every map's way ends in its second pass's last `go` span; at `speed` px/s through each of its `go` spans,
 * stood still in the pause between them; the client plays the recording from the drive's start, once a pass): every
 * build within `crush` px of it is crushed and `refund` of what it cost paid back (`Build.paid`), a gardener within
 * `body` px is killed (ADR-0186 amended), and all it digs is its trench (`trenchOf`).
 * Nothing stops it, and the night doesn't fall while it drives; its day lasts `dayTicks` (time to build a maze for the
 * second stage's rule, `walksRound`). From the night after it some of the dead rise from its trench as well as from the
 * graveyards (`HOLES`): all of it but within `clear` px of the Greenhouse, `heal` px more grown back round the
 * Greenhouse each dawn (`trenchOpen`), until it is all filled in.
 */
export const CHOMPER = {
  night: 40,
  wait: ticks(4),
  speed: 100,
  refund: 0.5,
  gait: {
    cycle: ticks(31.6),
    go: [
      [ticks(0.5), ticks(14)],
      [ticks(18.1), ticks(30)],
    ],
  },
  crush: 22,
  body: 20,
  dayTicks: ticks(120),
  clear: 7 * TILE_SIZE,
  heal: 2 * TILE_SIZE,
} as const;

/**
 * The holes in the ground the dead climb out of in the second stage (ADR-0186): Chomper's trench (`trenchOpen`) and
 * tonight's meteor craters (`NIGHT_METEORS`). While any is open, `share` of the night's dead rise from one instead of their
 * graveyard (no boss, nor a body wider than `body` px round: it would wedge in a maze, ADR-0098 amended); the night's
 * crowd is `crowd` times what it was (`nightBudget`), so the graveyards keep theirs and the holes bring more.
 */
export const HOLES = { share: 0.3, crowd: 1.35, body: 6 } as const;

/**
 * Zombie Nights' meteors (ADR-0186): from the second stage's first night (`walksRound`) `count` of them fall each night, one more every
 * `every` nights, at most `max`, spread through its rising (`spawnTicks`). Each is drawn (hidden: `world.secret`) onto an
 * open cell `near` to `far` px from the Greenhouse with no build on it, marked on the ground `warnTicks` before it lands;
 * where it lands every build within `radius` px loses `buildShare` of its health (mended at dawn as any build), a
 * gardener `damage` and is thrown, and its crater is a hole the dead climb out of (`HOLES`) until dawn.
 */
export const NIGHT_METEORS = {
  count: 3,
  every: 4,
  max: 8,
  warnTicks: ticks(2),
  near: 128,
  far: 420,
  radius: 26,
  buildShare: 0.5,
  damage: 30,
} as const;

/** How many meteors fall on night `n` (`NIGHT_METEORS`): none before the second stage. */
export const meteorCount = (n: number): number =>
  walksRound(n)
    ? Math.min(NIGHT_METEORS.max, NIGHT_METEORS.count + Math.floor((n - NIGHTS.mazeFrom) / NIGHT_METEORS.every))
    : 0;

/**
 * The night tick meteor `k` of night `n`'s is marked at (`meteorCount`): spread evenly through its rising, the first a
 * little into it (after nightfall's headline).
 */
export const meteorAt = (n: number, k: number): number =>
  Math.round(((k + 0.5) * spawnTicks(n)) / Math.max(1, meteorCount(n)));

/**
 * Day `n`'s length in ticks in a run from night `from` (`NIGHTS.dayTicks`, from `shortDayFrom` `shortDayTicks`; Chomper's
 * day is longer, `CHOMPER.dayTicks`; the run's first day whole).
 */
export const dayTicks = (n: number, from = 1): number =>
  n === CHOMPER.night + 1
    ? CHOMPER.dayTicks
    : n === from || n < NIGHTS.shortDayFrom
      ? NIGHTS.dayTicks
      : NIGHTS.shortDayTicks;

const CHOMPER_LINES = new WeakMap<object, LaneLine | null>();

/** Chomper's way on `map` (`MapDef.chomper`) as a line through its cells' centres, world px; null without one. */
export function chomperLine(map: Pick<MapDef, 'chomper'>): LaneLine | null {
  const route = map.chomper?.route;
  if (!route) return null;
  const known = CHOMPER_LINES.get(route);
  if (known !== undefined) return known;
  const points = route.map(([cx, cy]) => ({ x: (cx + 0.5) * TILE_SIZE, y: (cy + 0.5) * TILE_SIZE }));
  let len = 0;
  for (let i = 1; i < points.length; i++) {
    const [a, b] = [points[i - 1], points[i]] as { x: number; y: number }[];
    len += hypot(b!.x - a!.x, b!.y - a!.y);
  }
  const line = points.length > 1 ? { points, len } : null;
  CHOMPER_LINES.set(route, line);
  return line;
}

/** Ticks Chomper moves in a pass of its gait (its `go` spans'). */
const gaitGo = (): number => CHOMPER.gait.go.reduce((n, [a, b]) => n + b - a, 0);

/** Chomper's drive on `map` in ticks: its wait, then its way as its gait goes, stood still in its pauses (0: none). */
export const chomperTicks = (map: Pick<MapDef, 'chomper'>): number => {
  const line = chomperLine(map);
  if (!line) return 0;
  const { cycle, go } = CHOMPER.gait;
  let left = (line.len / CHOMPER.speed) * TICK_RATE;
  let t = 0;
  for (;;)
    for (const [a, b] of go) {
      if (left <= b - a) return CHOMPER.wait + Math.ceil(t + a + left);
      left -= b - a;
      if (b === go[go.length - 1]?.[1]) t += cycle;
    }
};

/**
 * Chomper's gait `t` ticks after its wait: which pass of its scream it is in (`scream`, the first 0), whether it moves
 * now (else stood still, the pause between its screams), and the ticks it has moved in all (`driven`).
 */
export function chomperLunge(t: number): { scream: number; moving: boolean; driven: number } {
  const { cycle, go } = CHOMPER.gait;
  const at = Math.max(0, t);
  const scream = Math.floor(at / cycle);
  const into = at - scream * cycle;
  let driven = scream * gaitGo();
  let moving = false;
  for (const [a, b] of go) {
    driven += Math.max(0, Math.min(into, b) - a);
    if (into >= a && into < b) moving = true;
  }
  return { scream, moving, driven };
}

/**
 * Where Chomper is `t` ticks into its drive on `map` (where it rolls in from while it waits), how far along its way
 * (`s`, px), the way it faces (`ux`, `uy`), whether its teeth are down (`digging`: on its way out, the last leg, where
 * its trench is), and its gait (`chomperLunge`: which pass of its scream, -1 while it waits; `moving`, or stood still).
 */
export function chomperAt(
  map: Pick<MapDef, 'chomper'>,
  t: number,
): {
  x: number;
  y: number;
  ux: number;
  uy: number;
  s: number;
  digging: boolean;
  scream: number;
  moving: boolean;
} | null {
  const line = chomperLine(map);
  if (!line) return null;
  const gait = chomperLunge(t - CHOMPER.wait);
  const s = Math.min(line.len, (gait.driven * CHOMPER.speed) / TICK_RATE);
  const at = alongLane(line, s);
  const pts = line.points;
  const [a, b] = [pts[pts.length - 2], pts[pts.length - 1]] as { x: number; y: number }[];
  return {
    ...at,
    s,
    digging: s >= line.len - hypot(b!.x - a!.x, b!.y - a!.y),
    scream: t > CHOMPER.wait ? gait.scream : -1,
    moving: t > CHOMPER.wait && gait.moving && s < line.len,
  };
}

const CHOMPER_CHEWS = new WeakMap<object, readonly { cell: number; s: number }[]>();

/**
 * The paving Chomper chews up on `map` (ADR-0180): every cobble cell whose middle it passes within `CHOMPER.crush` px
 * of, and how far along its way it first does (`s`, px). Only a look: to the rules torn-up paving is the road it was.
 */
export function chomperChews(map: MapDef): readonly { cell: number; s: number }[] {
  const line = chomperLine(map);
  if (!line || !map.chomper) return [];
  const known = CHOMPER_CHEWS.get(map.chomper);
  if (known) return known;
  const first = new Map<number, number>();
  const reach = Math.ceil(CHOMPER.crush / TILE_SIZE);
  for (let s = 0; s <= line.len; s += 2) {
    const { x, y } = alongLane(line, s);
    const [cx, cy] = [Math.floor(x / TILE_SIZE), Math.floor(y / TILE_SIZE)];
    for (let dy = -reach; dy <= reach; dy++)
      for (let dx = -reach; dx <= reach; dx++) {
        const [ux, uy] = [cx + dx, cy + dy];
        const cell = uy * map.width + ux;
        if (first.has(cell) || groundAt(map, ux, uy) !== Ground.Cobble) continue;
        if (hypot((ux + 0.5) * TILE_SIZE - x, (uy + 0.5) * TILE_SIZE - y) <= CHOMPER.crush) first.set(cell, s);
      }
  }
  const out = [...first].map(([cell, s]) => ({ cell, s }));
  CHOMPER_CHEWS.set(map.chomper, out);
  return out;
}

const TRENCHES = new WeakMap<object, readonly { cell: number; far: number; at: number }[]>();

/**
 * Chomper's trench on `map` (ADR-0186): every open cell of the yard its way digs through, nearest the Greenhouse first,
 * how far from it (px) and where along its way (`at`: the order it first dug them in).
 */
function trench(map: MapDef): readonly { cell: number; far: number; at: number }[] {
  const route = map.chomper?.route;
  if (!route) return [];
  const known = TRENCHES.get(route);
  if (known) return known;
  const home = map.bases[0] ?? { x: 0, y: 0 };
  const rows = yardRows(map);
  const cells = new Set<number>();
  for (let i = 1; i < route.length; i++) {
    const [[x0, y0], [x1, y1]] = [route[i - 1], route[i]] as [readonly [number, number], readonly [number, number]];
    const [dx, dy] = [Math.sign(x1 - x0), Math.sign(y1 - y0)];
    for (let j = 0; j <= Math.abs(x1 - x0) + Math.abs(y1 - y0); j++) {
      const [cx, cy] = [x0 + dx * j, y0 + dy * j];
      if (cx >= 0 && cy >= 0 && cx < map.width && cy < rows && !isSolid(map, cx, cy)) cells.add(cy * map.width + cx);
    }
  }
  const out = [...cells]
    .map((cell, at) => ({
      cell,
      at,
      far: hypot(
        ((cell % map.width) + 0.5) * TILE_SIZE - home.x,
        (Math.floor(cell / map.width) + 0.5) * TILE_SIZE - home.y,
      ),
    }))
    .sort((a, b) => a.far - b.far || a.cell - b.cell);
  TRENCHES.set(route, out);
  return out;
}

/** Chomper's trench on `map` (ADR-0186): every open cell its way digs (`cy * width + cx`), nearest the Greenhouse first. */
export const trenchOf = (map: MapDef): number[] => trench(map).map((t) => t.cell);

/**
 * Where tonight's dead from the trench `cells` (`trenchOpen`'s) start their way in, and its marker stands: the middle of
 * its open cell nearest the Greenhouse (world px).
 */
export function trenchMouth(map: Pick<MapDef, 'width'>, cells: readonly number[]): { x: number; y: number } {
  const c = cells[0] ?? 0;
  return { x: ((c % map.width) + 0.5) * TILE_SIZE, y: (Math.floor(c / map.width) + 0.5) * TILE_SIZE };
}

/**
 * The trench's cells still open on night `n` (and the day before it; ADR-0186): none before Chomper has come, then all
 * of it farther than `CHOMPER.clear` px from the Greenhouse, `CHOMPER.heal` px more grown back round it each dawn after.
 */
export function trenchOpen(map: MapDef, n: number): number[] {
  if (n <= CHOMPER.night) return [];
  const healed = CHOMPER.clear + CHOMPER.heal * (n - CHOMPER.night - 1);
  return trench(map).flatMap((t) => (t.far > healed ? [t.cell] : []));
}

/**
 * The trench open on night `n` (`trenchOpen`) in the order Chomper dug it along its way: where tonight's dead from it are
 * spread along all of it, every piece of it bringing its own (sim `rise`, ADR-0186 amended).
 */
export function trenchAlong(map: MapDef, n: number): number[] {
  const open = new Set(trenchOpen(map, n));
  return trench(map)
    .filter((t) => open.has(t.cell))
    .sort((a, b) => a.at - b.at)
    .map((t) => t.cell);
}

/**
 * Hell on the farm (ADR-0187). Chomper's drive sets the yard ablaze: a fire spreads from its way at `spread` px/s (each
 * cell `jitter` s give or take: a ragged front; slow enough that, its way crossing the whole yard, the fire takes most of
 * the drive, ADR-0186 amended) and `rain` fireballs come down on the farm as it goes, on what would burn last
 * (`hellFire`), so all of it has burnt `done` s before it leaves. The farm is hell from then until the dawn
 * after night `until`, when it heals: its grass ash, its trees burnt to the trunk (the client's look), its hedges and
 * ponds pits of lava (`lavaCells`, `CellFlag.Lava`). Every foot but a gardener's stays out of a pit, and shots and sight
 * go over it; a gardener `lava.edge` px into one falls in, held `lava.fallTicks` (a stun), then is put back on the
 * ground it fell from, `lava.damage` health gone.
 */
export const HELL = {
  until: 65,
  spread: 10,
  jitter: 0.35,
  rain: 12,
  done: 1.5,
  lava: { edge: 3, fallTicks: ticks(0.6), damage: 60 },
} as const;

/** Is the farm hell on day `n` (ADR-0187): from Chomper's day (as its fire reaches each cell) to the dawn after `HELL.until`? */
export const hellOn = (n: number): boolean => n > CHOMPER.night && n <= HELL.until;

/** Chomper's fire on a map (ADR-0187, `hellFire`). */
export interface HellFire {
  /** The tick of its drive the fire reaches each cell at (`cy * width + cx`); -1 past the yard (an arena's rows). */
  at: Int32Array;
  /** Its fireballs, the first first: the cell each comes down on and the tick of its drive it lands. */
  rain: readonly { cell: number; at: number }[];
}

const HELL_FIRES = new WeakMap<object, HellFire>();

/**
 * Chomper's fire on `map` (ADR-0187, `HELL`): it starts in each cell of the yard its way crosses as it first passes it,
 * and spreads from there over everything at `spread` px/s; each of its `rain` fireballs, evenly through its drive, comes
 * down on the hedge or tree the fire would reach last (inside the yard's rim; any cell once those burn in time) and
 * starts one there. A cell burns as the first fire reaches it, its jitter give or take, never before Chomper rolls in nor
 * later than `done` s before it leaves. The same on the server and every client: hashes, no draw.
 */
export function hellFire(map: MapDef): HellFire {
  const key = map.chomper;
  const known = key ? HELL_FIRES.get(key) : undefined;
  if (known) return known;
  const w = map.width;
  const rows = yardRows(map);
  const at = new Int32Array(w * map.height).fill(-1);
  const total = chomperTicks(map);
  if (!key || total === 0) return { at, rain: [] };
  const deadline = total - HELL.done * TICK_RATE;
  // Ticks for the fire to go a px; when it gets to each cell of the yard, from what is burning so far.
  const pace = TICK_RATE / HELL.spread;
  const burn = new Float64Array(w * rows).fill(Number.POSITIVE_INFINITY);
  const light = (i: number, t: number) => {
    const [sx, sy] = [i % w, Math.floor(i / w)];
    for (let cy = 0; cy < rows; cy++)
      for (let cx = 0; cx < w; cx++) {
        const v = t + hypot(cx - sx, cy - sy) * TILE_SIZE * pace;
        if (v < (burn[cy * w + cx] as number)) burn[cy * w + cx] = v;
      }
  };
  const passed = new Map<number, number>();
  for (let t = CHOMPER.wait + 1; t <= total; t++) {
    const p = chomperAt(map, t);
    const [cx, cy] = p ? [Math.floor(p.x / TILE_SIZE), Math.floor(p.y / TILE_SIZE)] : [-1, -1];
    if (cx >= 0 && cy >= 0 && cx < w && cy < rows && !passed.has(cy * w + cx)) passed.set(cy * w + cx, t);
  }
  for (const [i, t] of passed) light(i, t);
  const trees = new Set(map.props.flatMap((p) => (p.kind === PropKind.Tree ? [p.cy * w + p.cx] : [])));
  const inner = (i: number) => i % w > 0 && i % w < w - 1 && i >= w && i < (rows - 1) * w;
  const fuel = (i: number) => inner(i) && (trees.has(i) || groundAt(map, i % w, Math.floor(i / w)) === Ground.Hedge);
  const last = (ok: (i: number) => boolean) => {
    let best = -1;
    for (let i = 0; i < burn.length; i++)
      if (ok(i) && (best < 0 || (burn[i] as number) > (burn[best] as number))) best = i;
    return best;
  };
  const rain: { cell: number; at: number }[] = [];
  for (let k = 0; k < HELL.rain; k++) {
    const t = Math.round(CHOMPER.wait + ((k + 1) * (deadline - CHOMPER.wait)) / (HELL.rain + 1));
    let cell = last(fuel);
    if (cell < 0 || (burn[cell] as number) <= t) cell = last(inner);
    if (cell < 0 || (burn[cell] as number) <= t) break;
    rain.push({ cell, at: t });
    light(cell, t);
  }
  for (let i = 0; i < burn.length; i++) {
    const jitter = ((hash32(i % w, Math.floor(i / w), 0x68656c6c) / 0x100000000) * 2 - 1) * HELL.jitter * TICK_RATE;
    at[i] = Math.round(Math.max(CHOMPER.wait + 1, Math.min(deadline, (burn[i] as number) + jitter)));
  }
  const fire = { at, rain };
  HELL_FIRES.set(key, fire);
  return fire;
}

const LAVA_CELLS = new WeakMap<object, readonly number[]>();

/** The yard's hedges and ponds (ADR-0187): the pits of lava they are in hell, the first Chomper's fire reaches first. */
export function lavaCells(map: MapDef): readonly number[] {
  const key = map.chomper;
  const known = key ? LAVA_CELLS.get(key) : undefined;
  if (known) return known;
  if (!key) return [];
  const { at } = hellFire(map);
  const out: number[] = [];
  for (let cy = 0; cy < yardRows(map); cy++)
    for (let cx = 0; cx < map.width; cx++) {
      const g = groundAt(map, cx, cy);
      if (g === Ground.Hedge || g === Ground.Water) out.push(cy * map.width + cx);
    }
  out.sort((a, b) => (at[a] as number) - (at[b] as number) || a - b);
  LAVA_CELLS.set(key, out);
  return out;
}

/**
 * `map`'s pits of lava from the first `had` of `lavaCells(map)` to the first `want` (ADR-0187): the cells between poured
 * or cooled (`setLava`, on the map's own copy).
 */
export function pourLava(map: MapDef, had: number, want: number): void {
  if (want !== had) setLava(map, lavaCells(map).slice(Math.min(had, want), Math.max(had, want)), want > had);
}

/** How many of `lavaCells(map)` (the first) are lava on day `n`, Chomper `chomper` ticks into its drive (ADR-0187). */
export function lavaCount(map: MapDef, n: number, chomper: number): number {
  if (!hellOn(n)) return 0;
  const cells = lavaCells(map);
  if (n > CHOMPER.night + 1) return cells.length;
  const { at } = hellFire(map);
  let k = 0;
  while (k < cells.length && (at[cells[k] as number] as number) <= chomper) k++;
  return k;
}

/**
 * Zombie Nights' apple crates (ADR-0098 amended): a reward for going out of the yard. `count` stand round the farm, one in
 * each of as many wedges round the Greenhouse, each on open grass hashed from the run's seed, `near` to `far` px from the
 * Greenhouse, `laneCells` cells clear of the lanes' dirt and `gateClear` px off any grave; each grows back where it stood
 * (`APPLE_CRATE.regrowTicks`). A gardener who breaks one is healed (`APPLE_CRATE.heal`) and paid `gold` + `goldPerNight` ×
 * the night's number; no tower, zombie or boss ever breaks one.
 */
export const ZOMBIE_CRATES = {
  count: 8,
  near: 240,
  far: 420,
  laneCells: 2,
  gateClear: 64,
  gold: 4,
  goldPerNight: 1,
} as const;

/**
 * A run's apple crates on zombies map `map` (`ZOMBIE_CRATES`), hashed from `seed`: in each wedge round its base, a cell
 * whose 3×3 is open grass, with no dirt or cobble within `laneCells`, `near`–`far` px from the base and `gateClear` px
 * off every gate (a wedge with none has no crate).
 */
export function fruitSpots(map: MapDef, seed: number): MapDef['crates'] {
  const base = map.bases[0];
  if (!base) return [];
  const { count, near, far, laneCells, gateClear } = ZOMBIE_CRATES;
  const grass = (cx: number, cy: number) => {
    const g = groundAt(map, cx, cy);
    return (g === Ground.Grass || g === Ground.Flowers) && !isSolid(map, cx, cy);
  };
  const wedges: { x: number; y: number }[][] = Array.from({ length: count }, () => []);
  for (let cy = 1; cy < yardRows(map) - 1; cy++)
    for (let cx = 1; cx < map.width - 1; cx++) {
      const x = (cx + 0.5) * TILE_SIZE;
      const y = (cy + 0.5) * TILE_SIZE;
      const d = hypot(x - base.x, y - base.y);
      if (d < near || d > far || map.gates.some((g) => hypot(g.x - x, g.y - y) < gateClear)) continue;
      let open = true;
      for (let dy = -laneCells; dy <= laneCells && open; dy++)
        for (let dx = -laneCells; dx <= laneCells && open; dx++) {
          const g = groundAt(map, cx + dx, cy + dy);
          open =
            g !== Ground.Dirt &&
            g !== Ground.Cobble &&
            (Math.max(Math.abs(dx), Math.abs(dy)) > 1 || grass(cx + dx, cy + dy));
        }
      if (!open) continue;
      const turn = (atan2(y - base.y, x - base.x) / (Math.PI * 2) + 1) % 1;
      (wedges[Math.floor(turn * count) % count] as { x: number; y: number }[]).push({ x, y });
    }
  return wedges.flatMap((cells, i) => {
    const c = cells[hash32(seed, i, 0x66727569) % Math.max(1, cells.length)];
    return c ? [{ ...c, kind: CrateKind.Apple }] : [];
  });
}

/** What a Zombie Nights apple crate pays its breaker on night `n` (`ZOMBIE_CRATES`). */
export const crateGold = (n: number): number => ZOMBIE_CRATES.gold + ZOMBIE_CRATES.goldPerNight * Math.max(1, n);

/**
 * The bosses go for the builds first (all but the Blight, which takes root at the first in its way): one within `hunt` px
 * of walking — off their lane if need be, round walls — they make for and break (the Thresher mows), the nearest first,
 * before the Greenhouse; none that near, they keep to their lane.
 */
export const SIEGE = { hunt: 200 } as const;

/** A zombie of the night's roster: which kind rises at which gate (a `gates` index), `at` ticks into the night. */
export interface ZombieSpawn {
  at: number;
  kind: ZombieKind;
  gate: number;
}

/**
 * The gates night `n` opens (as indices into the map's `gates`). A map whose lanes stay (ADR-0176, `MapDef.gateNights`):
 * every gate whose night has come, for good. Else (Moonlit Farm) `gateCount` of them, neighbours round the Greenhouse,
 * from a gate the night draws (the same every run, ADR-0168) — the dead come from one side of the farm, another most
 * nights, so a defence is turned to face them each day (a build is moved for nothing). Known at the dawn before: nobody
 * builds blind.
 */
export function nightGates(map: Pick<MapDef, 'gates' | 'bases' | 'gateNights'>, n: number): number[] {
  const opens = map.gateNights;
  if (opens) return map.gates.flatMap((_, i) => ((opens[i] ?? 1) <= Math.max(1, n) ? [i] : []));
  const base = map.bases[0] ?? { x: 0, y: 0 };
  // How far round from straight up, clockwise on screen (y grows down): 0 … 2π, from half a gate's share of the circle
  // short of it, so the gate up there comes first though it stands a little either side (the Greenhouse stands where
  // its lanes cross, half a cell off the gate's: ADR-0206 amended).
  const turn = (g: { x: number; y: number }) =>
    (atan2(g.x - base.x, base.y - g.y) + Math.PI / map.gates.length + Math.PI * 2) % (Math.PI * 2);
  const order = map.gates.map((g, i) => ({ i, a: turn(g) })).sort((p, q) => p.a - q.a);
  if (order.length === 0) return [];
  const first = Math.floor((hash32(n, 0x67617465) / 0x100000000) * order.length);
  return Array.from(
    { length: gateCount(n, order.length) },
    (_, k) => (order[(first + k) % order.length] as { i: number }).i,
  ).sort((a, b) => a - b);
}

/**
 * How many steps either side a lane's middle is evened out over (`laneOf`), and how wide (cells) the road may be across
 * where its middle is measured (wider: a fork's mouth, a graveyard, the plaza).
 */
const LANE_EVEN = 1;
const LANE_WIDE = 3;
/** How far (cells) round its gate a lane's graveyard ground reaches: its road starts past it. */
const LANE_GRAVE = 2.5;
/** A lane's corner turns at least this far (radians); less, and its road runs on (`straight`). */
const LANE_TURN = 0.3;

/** A lane as a line (ADR-0176): world px from its gate to the Greenhouse, and its length (px). */
export interface LaneLine {
  points: readonly { x: number; y: number }[];
  len: number;
}

/**
 * Gate `gate`'s lane (ADR-0176): the dirt the dead walk from it to the Greenhouse with nothing built (the fewest cells
 * over the lanes, as their field takes it), down the middle of the road (each step moved to the middle of the road
 * round it), to the plaza's edge and on to the Greenhouse, its straight runs one segment each. What the client paints
 * tonight's side down (its chevrons, the minimap's roads) and a lesson's sign looks along. Null without a Greenhouse or a
 * way.
 */
export function laneOf(map: MapDef, gate: number): LaneLine | null {
  const base = map.bases[0];
  const g = map.gates[gate];
  if (!base || !g) return null;
  const w = map.width;
  const rows = yardRows(map);
  const road = (cx: number, cy: number) => {
    if (cx < 0 || cy < 0 || cx >= w || cy >= rows || isSolid(map, cx, cy)) return false;
    const ground = groundAt(map, cx, cy);
    return ground === Ground.Dirt || ground === Ground.Cobble;
  };
  const start = Math.floor(g.y / TILE_SIZE) * w + Math.floor(g.x / TILE_SIZE);
  const from = new Int32Array(w * rows).fill(-1);
  from[start] = start;
  const queue = [start];
  let end = -1;
  for (let q = 0; q < queue.length && end < 0; q++) {
    const c = queue[q] as number;
    const cx = c % w;
    const cy = (c - cx) / w;
    if (hypot((cx + 0.5) * TILE_SIZE - base.x, (cy + 0.5) * TILE_SIZE - base.y) <= 30) end = c;
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const n = c + dy * w + dx;
      if (road(cx + dx, cy + dy) && from[n] === -1) {
        from[n] = c;
        queue.push(n);
      }
    }
  }
  if (end < 0) return null;
  const cells: number[] = [];
  for (let c = end; c !== start; c = from[c] as number) cells.push(c);
  cells.push(start);
  cells.reverse();
  // Down the middle: each step moved across the road (square to the way it runs there) to the middle of it, where the
  // road is a road's width (a fork's mouth, the graveyard and the plaza keep the last step's offset); a diagonal's
  // staircase evened out over the steps either side; from the plaza's edge straight on to the Greenhouse.
  const at = (c: number) => ({ cx: c % w, cy: Math.floor(c / w) });
  const along = cells.findIndex((c) => groundAt(map, at(c).cx, at(c).cy) === Ground.Cobble);
  const kept = along < 0 ? cells : cells.slice(0, along + 1);
  const mids: { x: number; y: number }[] = [];
  let off = 0;
  for (let i = 0; i < kept.length; i++) {
    const p = at(kept[i] as number);
    const a0 = at(kept[Math.max(0, i - 2)] as number);
    const a1 = at(kept[Math.min(kept.length - 1, i + 2)] as number);
    const dl = hypot(a1.cx - a0.cx, a1.cy - a0.cy) || 1;
    const [nx, ny] = [-(a1.cy - a0.cy) / dl, (a1.cx - a0.cx) / dl];
    // The road cells beyond this one either way across it: the middle is half their difference over.
    const reach = (way: number) => {
      let t = 0;
      while (t < 3 && road(Math.floor(p.cx + 0.5 + nx * way * (t + 1)), Math.floor(p.cy + 0.5 + ny * way * (t + 1))))
        t++;
      return t;
    };
    const [plus, minus] = [reach(1), reach(-1)];
    if (plus + minus + 1 <= LANE_WIDE) off = (plus - minus) / 2;
    mids.push({ x: (p.cx + 0.5 + nx * off) * TILE_SIZE, y: (p.cy + 0.5 + ny * off) * TILE_SIZE });
  }
  // The road proper: past the graveyard's ground round the gate, short of the plaza.
  const out = mids.findIndex((q) => hypot(q.x - g.x, q.y - g.y) > LANE_GRAVE * TILE_SIZE);
  const points = mids.slice(Math.max(0, out)).map((_, j, rest) => {
    const near = rest.slice(Math.max(0, j - LANE_EVEN), j + LANE_EVEN + 1);
    return {
      x: near.reduce((s, q) => s + q.x, 0) / near.length,
      y: near.reduce((s, q) => s + q.y, 0) / near.length,
    };
  });
  // A segment may not leave the road (a bend's corner keeps its points; a diagonal's staircase is one line).
  const onRoad = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const l = hypot(b.x - a.x, b.y - a.y);
    for (let t = 0; t <= l; t += 4)
      if (
        !road(
          Math.floor((a.x + ((b.x - a.x) * t) / l) / TILE_SIZE),
          Math.floor((a.y + ((b.y - a.y) * t) / l) / TILE_SIZE),
        )
      )
        return false;
    return true;
  };
  const line = [{ x: g.x, y: g.y }, ...centred(straight(simplify(points, 6, onRoad)), road), { x: base.x, y: base.y }];
  let len = 0;
  for (let i = 1; i < line.length; i++) {
    const a = line[i - 1] as { x: number; y: number };
    const b = line[i] as { x: number; y: number };
    len += hypot(b.x - a.x, b.y - a.y);
  }
  return { points: line, len };
}

/** Where a lane is `s` px along it (clamped to it), and which way it runs there (unit). */
export function alongLane(lane: LaneLine, s: number): { x: number; y: number; ux: number; uy: number } {
  const pts = lane.points;
  let left = Math.max(0, s);
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1] as { x: number; y: number };
    const b = pts[i] as { x: number; y: number };
    const l = hypot(b.x - a.x, b.y - a.y);
    if (l === 0) continue;
    if (left <= l || i === pts.length - 1) {
      const t = Math.min(1, left / l);
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, ux: (b.x - a.x) / l, uy: (b.y - a.y) / l };
    }
    left -= l;
  }
  const p = pts[0] ?? { x: 0, y: 0 };
  return { x: p.x, y: p.y, ux: 1, uy: 0 };
}

/** `line` with each corner of under `LANE_TURN` radians left out: a road that runs on is one segment. */
function straight(line: readonly { x: number; y: number }[]): { x: number; y: number }[] {
  const out = [...line];
  for (let i = 1; i < out.length - 1; ) {
    const [a, b, c] = [out[i - 1], out[i], out[i + 1]] as { x: number; y: number }[];
    const turn = Math.abs(atan2((b as { y: number }).y - a!.y, b!.x - a!.x) - atan2(c!.y - b!.y, c!.x - b!.x));
    if (Math.min(turn, Math.PI * 2 - turn) < LANE_TURN) out.splice(i, 1);
    else i++;
  }
  return out;
}

/**
 * A lane's `line` with each segment moved across to the middle of the road beside it (the middle half of its cells'
 * offsets, so a fork's mouth or a crossing moves nothing), its corners where neighbouring segments now meet.
 */
function centred(
  line: readonly { x: number; y: number }[],
  road: (cx: number, cy: number) => boolean,
): { x: number; y: number }[] {
  const T = TILE_SIZE;
  const segs = line.slice(1).map((b, i) => {
    const a = line[i] as { x: number; y: number };
    const l = hypot(b.x - a.x, b.y - a.y) || 1;
    const [ux, uy] = [(b.x - a.x) / l, (b.y - a.y) / l];
    const at = (s: number, t: number) =>
      road(Math.floor((a.x + ux * s - uy * t) / T), Math.floor((a.y + uy * s + ux * t) / T));
    // The middle across the road at each half cell along it, in its first half and its second.
    const mids: [number[], number[]] = [[], []];
    for (let s = T / 2; s < l - T / 2; s += T / 2) {
      if (!at(s, 0)) continue;
      let lo = 0;
      let hi = 0;
      while (lo > -2 * T && at(s, lo - 1)) lo--;
      while (hi < 2 * T && at(s, hi + 1)) hi++;
      // A road's width across (a fork's mouth, a crossing are wider: they say nothing of its middle).
      if (hi - lo <= 2.5 * T) mids[s < l / 2 ? 0 : 1].push((lo + hi + 1) / 2);
    }
    // Each half's middle (the middle half of its offsets), at its middle: the segment through both, end to end.
    const [o0, o1] = mids.map((m) => {
      m.sort((p, q) => p - q);
      const half = m.slice(Math.floor(m.length / 4), Math.ceil((m.length * 3) / 4));
      return half.length > 0 ? half.reduce((p, q) => p + q, 0) / half.length : null;
    });
    const [start, end] = [o0 ?? o1 ?? 0, o1 ?? o0 ?? 0];
    const [offA, offB] = [start - (end - start) / 2, end + (end - start) / 2];
    const ax = a.x - uy * offA;
    const ay = a.y + ux * offA;
    const bx = b.x - uy * offB;
    const by = b.y + ux * offB;
    const nl = hypot(bx - ax, by - ay) || 1;
    return { x: ax, y: ay, ux: (bx - ax) / nl, uy: (by - ay) / nl, l: nl };
  });
  const out: { x: number; y: number }[] = [];
  segs.forEach((g, i) => {
    const prev = segs[i - 1];
    if (!prev) {
      out.push({ x: g.x, y: g.y });
      return;
    }
    // Where this segment's line meets the last one's (nearly parallel: half way between their ends).
    const cross = prev.ux * g.uy - prev.uy * g.ux;
    const ex = prev.x + prev.ux * prev.l;
    const ey = prev.y + prev.uy * prev.l;
    if (Math.abs(cross) < 0.2) out.push({ x: (ex + g.x) / 2, y: (ey + g.y) / 2 });
    else {
      const t = ((g.x - prev.x) * g.uy - (g.y - prev.y) * g.ux) / cross;
      out.push({ x: prev.x + prev.ux * t, y: prev.y + prev.uy * t });
    }
  });
  const last = segs.at(-1);
  if (last) out.push({ x: last.x + last.ux * last.l, y: last.y + last.uy * last.l });
  return out;
}

/**
 * `points` with every point within `eps` px of the line between its kept neighbours left out (Ramer–Douglas–Peucker),
 * so long as that line is `ok`.
 */
function simplify(
  points: readonly { x: number; y: number }[],
  eps: number,
  ok: (a: { x: number; y: number }, b: { x: number; y: number }) => boolean,
): { x: number; y: number }[] {
  if (points.length < 3) return [...points];
  const a = points[0] as { x: number; y: number };
  const b = points[points.length - 1] as { x: number; y: number };
  const l = hypot(b.x - a.x, b.y - a.y) || 1;
  let far = 0;
  let at = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const p = points[i] as { x: number; y: number };
    const d = Math.abs((b.x - a.x) * (a.y - p.y) - (a.x - p.x) * (b.y - a.y)) / l;
    if (d > far) {
      far = d;
      at = i;
    }
  }
  if (far <= eps && ok(a, b)) return [a, b];
  if (far === 0) at = points.length >> 1;
  return [...simplify(points.slice(0, at + 1), eps, ok).slice(0, -1), ...simplify(points.slice(at), eps, ok)];
}

/**
 * Night `n`'s zombies against a party of `players` (ADR-0098), in the order they rise: its budget (`nightBudget` ×
 * `partyCount`) filled with groups of the kinds come by then, each picked by `weight` (`spotlight` times that on its first
 * night), its size within its `group` (and what is left of the budget), at one of the night's `gates` in turn; the groups
 * shared out between its `waveCount` waves, evenly through `spawnTicks` (the first wave at once), a wave's groups within
 * `waveTicks`, a group's zombies `groupGapTicks` apart (a leader's crowd, `ZombieDef.escort`, right behind it). A boss
 * night adds its bosses (`nightBosses`) with its second wave. Pure, and the same every run of a week (ADR-0168, ADR-0226):
 * the night and the week's `deck` (its variants' first nights, `firstNight`; none without one) pick everything.
 */
export function nightRoster(n: number, players: number, gates: readonly number[], deck?: Deck | null): ZombieSpawn[] {
  const rng = (i: number) => hash32(n, i, 0x7a6f) / 0x100000000;
  let budget = nightBudget(n) * partyCount(players);
  const kinds = ZOMBIES.filter((z) => z.weight > 0 && firstNight(z, deck) <= n);
  const rush = rushOn(n);
  const dealt = dealtOn(n, deck);
  const hell = hellOn(n);
  const weight = (z: ZombieDef) =>
    rush.includes(z.kind)
      ? z.weight * LONG.rush
      : (z.weight *
          (firstNight(z, deck) === n || dealt.includes(z.kind) ? NIGHTS.spotlight : 1) *
          (z.hell && hell ? z.hell : 1)) /
        (z.fodder ? 1 + NIGHTS.fodderFade * (n - 1) : 1);
  const groups: { kind: ZombieKind; size: number }[] = [];
  const total = kinds.reduce((s, z) => s + weight(z), 0);
  for (let i = 0; budget > 0.25 && i < 10_000; i++) {
    let r = rng(i * 3) * total;
    let def = kinds[0] as ZombieDef;
    for (const z of kinds) {
      r -= weight(z);
      if (r < 0) {
        def = z;
        break;
      }
    }
    // A kind too dear for what is left: the cheapest one fills it.
    if (def.cost > budget) def = kinds.reduce((a, z) => (z.cost < a.cost ? z : a));
    const [lo, hi] = def.group;
    const want = lo + Math.floor(rng(i * 3 + 1) * (hi - lo + 1));
    const size = Math.max(1, Math.min(want, Math.floor(budget / def.cost)));
    groups.push({ kind: def.kind, size });
    budget -= size * def.cost;
  }
  const window = spawnTicks(n);
  const waves = waveCount(n);
  const waveAt = (w: number) => NIGHTS.firstSpawnTicks + (waves > 1 ? (w / (waves - 1)) * window : 0);
  const out: ZombieSpawn[] = [];
  const g = gates.length > 0 ? gates : [0];
  groups.forEach((group, i) => {
    // Its wave, and where in the wave's rush (the first group right on time: the night opens at once).
    const f = (i * waves) / groups.length;
    const wave = Math.floor(f);
    const jitter = i === 0 ? 0 : (rng(i * 7 + 2) - 0.5) * ticks(0.6);
    const at = Math.max(NIGHTS.firstSpawnTicks, Math.round(waveAt(wave) + (f - wave) * NIGHTS.waveTicks + jitter));
    const gate = g[(i + Math.floor(rng(5) * g.length)) % g.length] as number;
    for (let k = 0; k < group.size; k++) out.push({ at: at + k * NIGHTS.groupGapTicks, kind: group.kind, gate });
    // A leader's crowd rises right behind it (ADR-0226).
    const escort = zombieDef(group.kind).escort;
    for (let k = 0; k < (escort?.count ?? 0) * group.size; k++)
      out.push({ at: at + (group.size + k) * NIGHTS.groupGapTicks, kind: escort?.kind ?? ZombieKind.Shambler, gate });
  });
  for (const [b, kind] of nightBosses(n).entries())
    out.push({
      at: Math.round(waveAt(Math.min(1, waves - 1))) + b * ticks(8),
      kind,
      gate: g[(b + Math.floor(rng(11) * g.length)) % g.length] as number,
    });
  // The hearses: one a wave in turn, each from the next gate.
  for (let h = 0; h < nightHearses(n, players); h++)
    out.push({
      at: Math.round(waveAt(h % waves)) + ticks(3) + Math.floor(h / waves) * ticks(5),
      kind: ZombieKind.Hearse,
      gate: g[(h + Math.floor(rng(13) * g.length)) % g.length] as number,
    });
  // The week's set pieces (ADR-0226): Bone Balloons a wave in turn, a Grave Robber with the first.
  for (let b = 0; b < nightBalloons(n, players, deck); b++)
    out.push({
      at: Math.round(waveAt(b % waves)) + ticks(2) + Math.floor(b / waves) * ticks(6),
      kind: ZombieKind.Balloon,
      gate: g[(b + Math.floor(rng(17) * g.length)) % g.length] as number,
    });
  for (let r = 0; r < nightRobbers(n, deck); r++)
    out.push({
      at: Math.round(waveAt(0)) + ticks(6) + r * ticks(9),
      kind: ZombieKind.Robber,
      gate: g[(r + Math.floor(rng(19) * g.length)) % g.length] as number,
    });
  return out.sort((a, b) => a.at - b.at);
}

/** Night `n`'s hearses alone: the script's (`RUN`, `ACT_NIGHTS`, a rush's), past the last one more every `endlessBossEvery`. */
const hearsesOn = (n: number): number => {
  if (n <= FINALE) return RUN[n - 1]?.hearses ?? 0;
  if (n > LONG.last) return hearsesOn(LONG.last) + Math.floor(endless(n) / NIGHTS.endlessBossEvery);
  const { hearses } = actNight(n);
  return (hearses ? hearses + actOf(n) - 2 : 0) + (rushOn(n).includes(ZombieKind.Hearse) ? LONG.funeral : 0);
};

/** How many hearses night `n` brings a party of `players` (`hearsesOn`, `partyCount` times as many). */
export const nightHearses = (n: number, players: number): number => Math.round(hearsesOn(n) * partyCount(players));

/**
 * The bosses night `n` brings: the script's (`RUN`); in a long act its night's (`ACT_NIGHTS`: one more each act past the
 * second, a parade's one of each); past the last `endlessBosses`, one more every `endlessBossEvery` nights (never fewer
 * than the last's). Drawn by the night from every boss, no kind twice in a night while another is left.
 */
export function nightBosses(n: number): ZombieKind[] {
  if (n <= FINALE) return [...(RUN[n - 1]?.bosses ?? [])];
  const kinds = ZOMBIES.filter((z) => z.boss).map((z) => z.kind);
  const slot = actNight(n);
  if (n <= LONG.last && slot.parade) return kinds;
  const count =
    n <= LONG.last
      ? slot.bosses
        ? slot.bosses + actOf(n) - 2
        : 0
      : Math.max(
          nightBosses(LONG.last).length,
          NIGHTS.endlessBosses + Math.floor((endless(n) - 1) / NIGHTS.endlessBossEvery),
        );
  const out: ZombieKind[] = [];
  for (let i = 0; i < count; i++) {
    const left = kinds.filter((k) => !out.includes(k));
    const pool = left.length > 0 ? left : kinds;
    out.push(pool[Math.floor((hash32(n, i, 0x626f7373) / 0x100000000) * pool.length)] as ZombieKind);
  }
  return out;
}

/**
 * The kinds night `n` brings for the first time (its question, ADR-0168), in `ZombieKind` order: the dead whose `from` it
 * is (a variant's: the night `deck` deals it first, ADR-0226), and the bosses it brings first. Past the finale, none but
 * the deck's.
 */
export const newOn = (n: number, deck?: Deck | null): ZombieKind[] => {
  const tonight = new Set(nightRoster(n, 1, [0], deck).map((s) => s.kind));
  return ZOMBIES.filter((z) => firstNight(z, deck) === n && tonight.has(z.kind)).map((z) => z.kind);
};

// ─── Builds ───────────────────────────────────────────────────────────────────────────────────────────

/** What a party builds (ADR-0098, ADR-0171): towers (`TOWERS`: bought whole, none of them a gun anyone holds) and fences. */
export const BuildKind = { Tower: 0, Fence: 1 } as const;
export type BuildKind = (typeof BuildKind)[keyof typeof BuildKind];

export interface BuildDef {
  readonly kind: BuildKind;
  readonly key: 'tower' | 'fence';
  readonly name: string;
  readonly maxHp: number;
  /** Its body zombies bump into (0: they walk over it). Gardeners never bump into a build. */
  readonly bodyRadius: number;
  /** What zombies' blows and acid hit (0: nothing hits it). */
  readonly hurtRadius: number;
  /** Zombies' flow field counts walking through its cell as this many 1/10 cells more (it has to be broken). */
  readonly pathCost: number;
  /** Minimap / UI colour. */
  readonly color: number;
}

/**
 * ponytail: no cap on builds (fences and towers as many as a party pays for); every build is in every snapshot, ~20 B a
 * fence: 600 fences add ~12 KB a snapshot, ~360 KB/s a player. The upgrade: send a build only when it changes.
 */
export const BUILDS: readonly BuildDef[] = [
  {
    kind: BuildKind.Tower,
    key: 'tower',
    name: 'Tower',
    maxHp: 360,
    bodyRadius: 7,
    hurtRadius: 8,
    pathCost: 90,
    color: 0x5a6988,
  },
  {
    kind: BuildKind.Fence,
    key: 'fence',
    name: 'Fence',
    maxHp: 260,
    bodyRadius: 8,
    hurtRadius: 8,
    pathCost: 60,
    color: 0xb86f50,
  },
];

export function buildDef(kind: number): BuildDef {
  return BUILDS[kind] ?? (BUILDS[0] as BuildDef);
}

// ─── Towers ───────────────────────────────────────────────────────────────────────────────────────────

/**
 * Zombie Nights' towers (ADR-0171): four, one job each, nobody can hold one; each good at everything, best at one
 * (ADR-0227: `TowerDef.special`). Each is bought whole at a flat price and fires a weapon only towers fire
 * (`TowerDef.weapon`: through the projectile path, or no projectile at all and its `shot` only a mark,
 * `WeaponDef.mark`). Each has two paths of three upgrades (`TowerDef.paths`), each path's bought in order: one path up
 * to its third, the other up to its first (ADR-0227, Bloons TD 6's crosspaths: `upgradeOf`); a tier sets numbers
 * (`TowerStats`) and may add one behaviour, and changes how the tower looks.
 */
export const TowerKind = { Sprinkler: 0, Beehive: 1, Sunflower: 2, Mortar: 3 } as const;
export type TowerKind = (typeof TowerKind)[keyof typeof TowerKind];

/** A build's `tower` when it is none (a fence): `Build.tower`, the wire's. */
export const NO_TOWER = 0xff;

/**
 * Which spray a Sprinkler's `shot` (SPRINKLER_SPRAY) marks, in its `power`: a plain one, a geyser (`TowerStats.geyser`)
 * or one that froze the soaked solid (`TowerStats.freeze`).
 */
export const SprayKind = { Plain: 0, Geyser: 1, Freeze: 2 } as const;
export type SprayKind = (typeof SprayKind)[keyof typeof SprayKind];

/**
 * What a tower does: every number its system reads, as bought (`TowerDef.base`) and as each upgrade sets them
 * (`TowerTier.stats`, laid over in order: `towerStats`). Ticks are the sim's; damage is before the night's
 * (`defenceScale`); "a second" numbers are per second. A behaviour no tier gave (its field left out) does nothing; a
 * tier that sets one keeps it up its path.
 */
export interface TowerStats {
  /** A hit's damage: a spray's, a sting's, a beam's tick's, a blast's at its centre. */
  readonly damage: number;
  /** Ticks between two of its shots: a spray, a bee let out, a beam's tick, a lob. */
  readonly every: number;
  /** How far it reaches, px to a zombie's edge: a Sprinkler's spray all round it, a hive's bees' hunt, a beam, a lob. */
  readonly reach: number;
  /** A Mortar lobs no nearer than this, px. */
  readonly minReach?: number;
  /** A blast's radius, px. */
  readonly radius?: number;
  /** A hit soaks the dead (`Frozen`: slowed) for this many ticks. */
  readonly soakTicks?: number;
  /** A hive's bees out at once, at most. */
  readonly bees?: number;
  /** It reaches flyers (ADR-0227): picks them as targets, and its spray, beam, blast and cluster land on them. */
  readonly flyers?: true;
  /** Its hits get through armour (a Bucket Head's pail, ADR-0227), as a beam's or a blast's do. */
  readonly armour?: true;
  /** It, and its bees, see Wisps (`ZombieDef.shrouded`) without a Daylight (ADR-0227). */
  readonly wisps?: true;
  /** A spray throws the dead out from it at this many px/s (never a boss, a heavy one or a mega boss's part). */
  readonly knock?: number;
  /** Every `nth` spray is a geyser: `damage` in place of the spray's, and the dead in it stunned for `stunTicks`. */
  readonly geyser?: { readonly nth: number; readonly damage: number; readonly stunTicks: number };
  /** Every `nth` spray freezes the soaked solid: stunned for `stunTicks`. */
  readonly freeze?: { readonly nth: number; readonly stunTicks: number };
  /** The dead frozen solid take `brittle` times the damage from everything (towers and gardeners). */
  readonly brittle?: number;
  /** A sting poisons: `damage` a second for `ticks`. */
  readonly poison?: { readonly damage: number; readonly ticks: number };
  /** Every `nth` sting leaves a honey puddle of `radius` px for `ticks` that soaks all in it. */
  readonly honey?: { readonly nth: number; readonly radius: number; readonly ticks: number };
  /** Every tower within `radius` px fires `rate` faster (a share, as the Fire rate card's). */
  readonly haste?: { readonly radius: number; readonly rate: number };
  /** Its beam goes through every one of the dead on its line. */
  readonly pierceAll?: true;
  /** A hit sets the dead on fire: `damage` a second for `ticks`. */
  readonly burn?: { readonly damage: number; readonly ticks: number };
  /** Beams at once, each at another of the dead (one when left out). */
  readonly beams?: number;
  /** Daylight: the ground within `radius` px is lit, and towers and bees in it see Wisps (`ZombieDef.shrouded`). */
  readonly light?: { readonly radius: number };
  /** Every tower within `radius` px hits `damage` harder (a share). */
  readonly boost?: { readonly radius: number; readonly damage: number };
  /** Its blast knocks armour off for good (`ZombieDef.armoured`, `EntityFlag.Cracked`). */
  readonly crack?: true;
  /** Its blast stuns the dead in it for this many ticks. */
  readonly stunTicks?: number;
  /** Its blast bursts into `count` more within `spread` px of it, each of `radius` px for `share` of its damage. */
  readonly cluster?: {
    readonly count: number;
    readonly share: number;
    readonly radius: number;
    readonly spread: number;
  };
  /** It leaves fire where it lands: `radius` px for `ticks`, `damage` a second to the dead in it. */
  readonly fire?: { readonly radius: number; readonly ticks: number; readonly damage: number };
}

/**
 * An upgrade: what it is called, what it does (`desc`: a few words a player gets at a glance, its card's row, no full
 * stop), its price, the numbers it sets (a tower's, a fence's).
 */
export interface TowerTier<S = TowerStats> {
  readonly name: string;
  readonly desc: string;
  readonly cost: number;
  readonly stats: Partial<S>;
}

/** One of a tower's (or a fence's) two paths: its three upgrades, bought in order. */
export interface TowerPath<S = TowerStats> {
  readonly key: string;
  readonly name: string;
  readonly tiers: readonly [TowerTier<S>, TowerTier<S>, TowerTier<S>];
}

export interface TowerDef {
  readonly kind: TowerKind;
  readonly key: 'sprinkler' | 'beehive' | 'sunflower' | 'mortar';
  readonly name: string;
  /** What it does in a few words, for the build bar's card. */
  readonly desc: string;
  /** What it can't do, in a few words: its weak spot (ADR-0184), on the build bar's card under what it is good for. */
  readonly weak: string;
  /**
   * What it is best at (ADR-0227): every hit of its (its weapon's, its blasts', its bees') on one of the dead of
   * `kinds` does `times` as much (`towerSpecialScale`); `label` names them on its card.
   */
  readonly special: { readonly kinds: readonly ZombieKind[]; readonly times: number; readonly label: string };
  readonly cost: number;
  /**
   * The run's day it goes on sale to a gardener whose account never got that far (ADR-0184): the day before the night
   * whose dead it answers first. Reached once, in any run, it is theirs from day 1 for good (`towerUnlocked`).
   */
  readonly unlock: number;
  /** What it fires (a `turret` weapon: nobody holds it). */
  readonly weapon: WeaponDef;
  readonly base: TowerStats;
  /** Path A, path B. */
  readonly paths: readonly [TowerPath, TowerPath];
  /** Its Paragon (ADR-0181): both paths at once, by what it is called. */
  readonly paragon: { readonly name: string };
}

/** The towers, by `TowerKind`. */
export const TOWERS: readonly TowerDef[] = [
  {
    kind: TowerKind.Sprinkler,
    key: 'sprinkler',
    name: 'Sprinkler',
    desc: 'Slows the dead, wets the ground: Diggers pop up',
    weak: 'Short reach, no armour. No flyers till upgraded',
    special: { kinds: [ZombieKind.Shambler, ZombieKind.Runner], times: 2, label: 'the crowd' },
    cost: 100,
    unlock: 1,
    weapon: SPRINKLER_SPRAY,
    base: { reach: 56, every: ticks(1), damage: 7, soakTicks: ticks(0.8) },
    paragon: { name: 'Monsoon' },
    paths: [
      {
        key: 'pressure',
        name: 'Pressure',
        tiers: [
          { name: 'Brass Nozzle', desc: 'Farther, harder', cost: 70, stats: { reach: 70, damage: 10 } },
          {
            name: 'Pressure Washer',
            desc: 'Knocks back',
            cost: 180,
            stats: { damage: 16, knock: 110, flyers: true },
          },
          {
            name: 'Geyser',
            desc: 'Launches them',
            cost: 450,
            stats: { reach: 80, geyser: { nth: 3, damage: 70, stunTicks: ticks(0.8) } },
          },
        ],
      },
      {
        key: 'frost',
        name: 'Frost',
        tiers: [
          { name: 'Cold Water', desc: 'Longer slow', cost: 80, stats: { soakTicks: ticks(2), reach: 62 } },
          {
            name: 'Ice Sprinkler',
            desc: 'Freezes them',
            cost: 200,
            stats: { freeze: { nth: 3, stunTicks: ticks(1) }, wisps: true },
          },
          {
            name: 'Blizzard',
            desc: 'Frozen take 2×',
            cost: 480,
            stats: { reach: 84, freeze: { nth: 2, stunTicks: ticks(1) }, brittle: 2 },
          },
        ],
      },
    ],
  },
  {
    kind: TowerKind.Beehive,
    key: 'beehive',
    name: 'Beehive',
    desc: 'Bees hunt flyers and Diggers underground',
    weak: 'Stings bounce off armour till upgraded. Needs eyes',
    special: { kinds: [ZombieKind.Crow, ZombieKind.Moth, ZombieKind.Balloon], times: 3, label: 'flyers' },
    cost: 150,
    unlock: 3,
    weapon: HIVE_BEE,
    base: { reach: 150, every: ticks(1.2), bees: 3, damage: 10, flyers: true },
    paragon: { name: 'Hive Queen' },
    paths: [
      {
        key: 'swarm',
        name: 'Swarm',
        tiers: [
          { name: 'Bigger Hive', desc: 'More bees', cost: 90, stats: { bees: 5, every: ticks(0.9) } },
          {
            name: 'Killer Bees',
            desc: 'Poison stings',
            cost: 220,
            stats: { damage: 18, poison: { damage: 3, ticks: ticks(3) }, armour: true },
          },
          {
            name: 'Hornet Nest',
            desc: 'Huge swarm',
            cost: 520,
            stats: { bees: 8, every: ticks(0.5), damage: 28 },
          },
        ],
      },
      {
        key: 'honey',
        name: 'Honey',
        tiers: [
          { name: 'Sticky Honey', desc: 'Stings slow', cost: 90, stats: { soakTicks: ticks(1.5) } },
          {
            name: 'Honey Pots',
            desc: 'Honey puddles',
            cost: 210,
            stats: { honey: { nth: 3, radius: 26, ticks: ticks(4) }, wisps: true },
          },
          {
            name: 'Royal Jelly',
            desc: 'Faster towers',
            cost: 420,
            stats: { haste: { radius: 110, rate: 0.35 } },
          },
        ],
      },
    ],
  },
  {
    kind: TowerKind.Sunflower,
    key: 'sunflower',
    name: 'Sunflower',
    desc: 'Burns one with a beam, armour too',
    weak: 'No flyers till upgraded. Moths smother it',
    // The big ones: Brutes, hearses and every night's boss (never a mega boss's part: no tower touches one).
    special: {
      kinds: ZOMBIES.filter(
        (z) => (z.boss && !z.king) || z.kind === ZombieKind.Brute || z.kind === ZombieKind.Hearse,
      ).map((z) => z.kind),
      times: 2,
      label: 'big ones',
    },
    cost: 220,
    unlock: 5,
    weapon: SUN_RAY,
    // A beam's tick every 3 ticks: 40 a second.
    base: { reach: 190, every: 3, damage: 4 },
    paragon: { name: 'High Noon' },
    paths: [
      {
        key: 'focus',
        name: 'Focus',
        tiers: [
          {
            name: 'Bright Petals',
            desc: 'Hotter, farther',
            cost: 110,
            stats: { damage: 6, reach: 210 },
          },
          {
            name: 'Solar Lance',
            desc: 'Pierces all',
            cost: 280,
            stats: { pierceAll: true, flyers: true },
          },
          {
            name: 'Solar Flare',
            desc: 'Burning beam',
            cost: 620,
            stats: { damage: 12, burn: { damage: 6, ticks: ticks(2) } },
          },
        ],
      },
      {
        key: 'bloom',
        name: 'Bloom',
        tiers: [
          { name: 'Twin Heads', desc: 'Two beams', cost: 140, stats: { beams: 2 } },
          {
            name: 'Daylight',
            desc: 'Reveals Wisps',
            cost: 230,
            stats: { light: { radius: 130 } },
          },
          {
            name: 'Sun Temple',
            desc: 'Boosts towers',
            cost: 520,
            stats: { light: { radius: 180 }, boost: { radius: 180, damage: 0.25 } },
          },
        ],
      },
    ],
  },
  {
    kind: TowerKind.Mortar,
    key: 'mortar',
    name: 'Pumpkin Mortar',
    desc: 'Blasts crowds far off',
    weak: 'No flyers till upgraded. Blind behind hedges',
    special: { kinds: [ZombieKind.Spitter, ZombieKind.Lobber, ZombieKind.Deadeye], times: 2, label: 'shooters' },
    cost: 280,
    unlock: 6,
    weapon: MORTAR_SHELL,
    base: { reach: 260, minReach: 48, every: ticks(2.6), radius: 34, damage: 45 },
    paragon: { name: 'Great Pumpkin' },
    paths: [
      {
        key: 'payload',
        name: 'Payload',
        tiers: [
          { name: 'Big Pumpkin', desc: 'Wider blast', cost: 120, stats: { radius: 44 } },
          {
            name: 'Heavy Shell',
            desc: 'Cracks pails',
            cost: 300,
            stats: { damage: 80, crack: true },
          },
          {
            name: 'Giant Pumpkin',
            desc: 'Stunning blast',
            cost: 680,
            stats: { radius: 62, damage: 180, stunTicks: ticks(0.7) },
          },
        ],
      },
      {
        key: 'patch',
        name: 'Patch',
        tiers: [
          { name: 'Quick Lob', desc: 'Fires faster', cost: 130, stats: { every: ticks(1.7) } },
          {
            name: 'Cluster Pumpkin',
            desc: '3 extra blasts',
            cost: 280,
            stats: { cluster: { count: 3, share: 0.4, radius: 24, spread: 30 }, flyers: true },
          },
          {
            name: "Jack-o'-Fire",
            desc: 'Leaves fire',
            cost: 560,
            stats: { fire: { radius: 32, ticks: ticks(3), damage: 8 } },
          },
        ],
      },
    ],
  },
];

export const towerDef = (kind: number): TowerDef => TOWERS[kind] ?? (TOWERS[0] as TowerDef);

/**
 * How many times as hard a hit of tower `tower`'s (a TowerKind; a fence, `NO_TOWER`, has no specialty) lands on one of
 * the dead of kind `kind` (ADR-0227): its specialty's `times` (`TowerDef.special`), else 1.
 */
export function towerSpecialScale(tower: number, kind: number): number {
  const special = TOWERS[tower]?.special;
  return special?.kinds.includes(kind as ZombieKind) ? special.times : 1;
}

/**
 * Is tower `kind` on sale to a gardener on day `day` of a run whose account had reached day `reached` before it
 * (ADR-0184: `TowerDef.unlock`, the further of the two)? Upgrading or feeding a mate's tower is never locked.
 */
export const towerUnlocked = (kind: number, day: number, reached: number): boolean =>
  Math.max(day, reached) >= towerDef(kind).unlock;

/** The towers that go on sale on day `day` to a gardener whose account had reached day `reached` (ADR-0184), if any. */
export const unlockedOn = (day: number, reached: number): TowerKind[] =>
  day > reached ? TOWERS.filter((t) => t.unlock === day && t.unlock > 1).map((t) => t.kind) : [];

/**
 * What a fence does (ADR-0171 amended): its health, as built (`BuildDef.maxHp`) and as each upgrade sets it; a Thorns
 * fence pricks a zombie that strikes it for `thorns` (before the night's `defenceScale`; nothing guards against it: it is
 * what the zombie touched), soaks it for `soakTicks` and sets it on fire (`burn`: damage a second for `ticks`).
 */
export interface FenceStats {
  readonly hp: number;
  readonly thorns?: number;
  readonly soakTicks?: number;
  readonly burn?: { readonly damage: number; readonly ticks: number };
}

/**
 * A fence's two paths of three upgrades (ADR-0171 amended), bought as a tower's (one path up to its third, the other up
 * to its first: ADR-0227's crosspaths), each tier of its main path changing how it looks (`t0`, `a1`-`a3`, `b1`-`b3`:
 * the client's `fencePicture`). Wall holds the dead longer; Thorns hurts those that strike it (a little tougher too).
 */
export const FENCE: { readonly paths: readonly [TowerPath<FenceStats>, TowerPath<FenceStats>] } = {
  paths: [
    {
      key: 'wall',
      name: 'Wall',
      tiers: [
        { name: 'Thick Planks', desc: 'Tougher', cost: 25, stats: { hp: 420 } },
        { name: 'Stone Wall', desc: 'Much tougher', cost: 60, stats: { hp: 700 } },
        { name: 'Iron Wall', desc: 'Toughest', cost: 140, stats: { hp: 1100 } },
      ],
    },
    {
      key: 'thorns',
      name: 'Thorns',
      tiers: [
        { name: 'Brambles', desc: 'Pricks attackers', cost: 30, stats: { hp: 330, thorns: 5 } },
        {
          name: 'Barbed Wire',
          desc: 'Pricks, slows',
          cost: 70,
          stats: { hp: 400, thorns: 9, soakTicks: ticks(1.5) },
        },
        {
          name: 'Firethorn',
          desc: 'Burns attackers',
          cost: 150,
          stats: { hp: 470, thorns: 13, burn: { damage: 8, ticks: ticks(3) } },
        },
      ],
    },
  ],
};

/** The two upgrade paths of a tower `tower` (content TowerKind), or a fence's (`NO_TOWER`); undefined: no such tower. */
export const pathsOf = (
  tower: number,
):
  | readonly [TowerPath<TowerStats>, TowerPath<TowerStats>]
  | readonly [TowerPath<FenceStats>, TowerPath<FenceStats>]
  | undefined => (tower === NO_TOWER ? FENCE.paths : TOWERS[tower]?.paths);

/**
 * A build's tier (`Build.tier`, the wire's `tier`, a saved run's), a tower's or a fence's. Its low 3 bits
 * (`tier & 7`) are its main path, which sets its look (its sprite's anim `t0`, `a1`-`a3`, `b1`-`b3`): 0 as bought,
 * `level` (1-3) upgrades of path `path` (0: A, 1: B) tiers 1-3 and 4-6, 7 a Paragon (`PARAGON.tier`, exactly).
 * `CROSS` (8) over them: the other path's first upgrade bought too (ADR-0227, Bloons TD 6's crosspaths: one path up
 * to 3, the other up to 1; the main path is the one further up, the first bought on a tie: `upgradedTier`).
 * Tiers 0-7 mean what they meant before crosspaths (saved runs hold them).
 */
export const towerTier = (path: number, level: number): number => (level <= 0 ? 0 : path * 3 + Math.min(3, level));

/** A tier's bit for its cross path's upgrade (ADR-0227, `towerTier`): the path it isn't on has its first. */
export const CROSS = 8;

/** The main path of tier `tier` (0: A, 1: B), or -1 (as bought). */
export const tierPath = (tier: number): number => ((tier & 7) === 0 ? -1 : Math.floor(((tier & 7) - 1) / 3));

/** How many of its main path's upgrades a build at tier `tier` has (0 as bought). */
export const tierLevel = (tier: number): number => ((tier & 7) === 0 ? 0 : (((tier & 7) - 1) % 3) + 1);

/** How many of its cross path's upgrades a build at tier `tier` has (ADR-0227): 0 or 1. */
export const crossOf = (tier: number): number => ((tier & CROSS) === 0 ? 0 : 1);

/** How many of path `path`'s (0: A, 1: B) upgrades a build at tier `tier` has; a Paragon has both paths whole. */
export const pathLevel = (tier: number, path: number): number =>
  isParagon(tier) ? 3 : tierPath(tier) === path ? tierLevel(tier) : crossOf(tier);

/**
 * The tier a build at tier `tier` stands at once its next upgrade on path `path` is bought (`upgradeOf` says whether it
 * may be): its main path the one further up, the first bought on a tie (1 and 1), `CROSS` once both paths have one.
 */
export function upgradedTier(tier: number, path: number): number {
  const a = pathLevel(tier, 0) + (path === 0 ? 1 : 0);
  const b = pathLevel(tier, 1) + (path === 1 ? 1 : 0);
  const main = a === b ? tierPath(tier) : a > b ? 0 : 1;
  return towerTier(main, Math.max(a, b)) | (Math.min(a, b) > 0 ? CROSS : 0);
}

/** Numbers where less is better, for `better`: ticks between shots, a Mortar's nearest lob. */
const LESS: ReadonlySet<string> = new Set(['every', 'minReach']);

/**
 * Upgrade numbers `stats` laid over `on` (ADR-0227): a number the better of the two (the higher; `LESS`'s the lower),
 * so a cross path's upgrade and the main path's never take back what the other gave; an object or a flag the later's.
 */
function better<S extends object>(on: S, stats: Partial<S>): S {
  const out = { ...on } as Record<string, unknown>;
  for (const [key, v] of Object.entries(stats)) {
    const was = out[key];
    out[key] =
      typeof v !== 'number' || typeof was !== 'number' ? v : LESS.has(key) ? Math.min(was, v) : Math.max(was, v);
  }
  return out as S;
}

/**
 * A build's numbers at tier `tier`: `base` as bought, then its cross path's first upgrade, then its main path's in
 * order, each laid over the last (`better`); a Paragon's (ADR-0181) every upgrade of both paths, A's then B's.
 */
function statsAt<S extends object>(base: S, paths: readonly [TowerPath<S>, TowerPath<S>], tier: number): S {
  let stats = base;
  for (const p of tierPath(tier) === 0 ? ([1, 0] as const) : ([0, 1] as const))
    for (const t of paths[p].tiers.slice(0, pathLevel(tier, p))) stats = better(stats, t.stats);
  return stats;
}

/**
 * Tower `kind`'s numbers at tier `tier`: as bought, then its upgrades (`statsAt`: the cross path's, then the main
 * path's); its Paragon's (ADR-0181) both paths' (how hard it hits by its degree is `paragonPower`'s).
 */
export function towerStats(kind: number, tier: number): TowerStats {
  const def = towerDef(kind);
  return statsAt(def.base, def.paths, tier);
}

/**
 * Paragons (ADR-0181, Bloons TD 6's): a tower that has had a path's last upgrade (crossed or not, ADR-0227) eats the
 * other towers of its kind within `radius` px of it (centre to centre; whoever on the team built them, ADR-0181
 * amended) — at least `minEaten` of them, for `price` more gold (by TowerKind) — and stands as its Paragon, tier
 * `tier`: both paths at once (`towerStats`), `hp` times as tough as its third upgrade, its meal's squares free. One of
 * each kind stands at a time, and it eats again (no price) whenever more of its kind stand round it. All the gold in it
 * (`Build.paid`: itself, its meals, its prices) is its degree, 1 to 100 (`paragonDegree`: a degree every `unit` gold),
 * and it hits `paragonPower` times as hard: `perDegree` more a degree. The party's gold has somewhere to go once the
 * yard is full.
 */
export const PARAGON = {
  tier: 7,
  radius: 72,
  minEaten: 2,
  price: [2000, 2500, 3000, 3500],
  hp: 4,
  unit: 3000,
  perDegree: 1,
} as const;

/** Is tier `tier` a Paragon's (ADR-0181)? */
export const isParagon = (tier: number): boolean => tier === PARAGON.tier;

/** What making tower `kind`'s Paragon costs over the towers it eats (ADR-0181). */
export const paragonPrice = (kind: number): number => PARAGON.price[kind] ?? PARAGON.price[0];

/** A Paragon's degree (1-100) with `paid` gold in it (ADR-0181). */
export const paragonDegree = (paid: number): number => Math.max(1, Math.min(100, Math.floor(paid / PARAGON.unit)));

/** How many times as hard a Paragon of degree `degree` hits as its numbers say (ADR-0181). */
export const paragonPower = (degree: number): number => 1 + (degree - 1) * PARAGON.perDegree;

/**
 * A fence's numbers at tier `tier`: as built, then its upgrades (`FENCE`; `statsAt`: the cross path's, the main's).
 * Its health is a pool, not a rate (ADR-0227): each path's upgrades add what they add to a fence as built, so a cross
 * path's first upgrade always makes it tougher (a Firethorn with Thick Planks: 470 + 160).
 */
export function fenceStats(tier: number): FenceStats {
  const built: FenceStats = { hp: buildDef(BuildKind.Fence).maxHp };
  const gain = (path: number): number =>
    statsAt(built, FENCE.paths, towerTier(path, pathLevel(tier, path))).hp - built.hp;
  return { ...statsAt(built, FENCE.paths, tier), hp: built.hp + gain(0) + gain(1) };
}

/**
 * The upgrade a tower `kind` (a fence: `NO_TOWER`) at tier `tier` takes next on path `path` (0: A, 1: B), or null: no
 * such path or tower, its path's last upgrade had (a Paragon has them all), or past the crosspaths' limit (ADR-0227: a
 * path goes past its first upgrade only while the other has at most one).
 */
export function upgradeOf(kind: number, tier: number, path: number): TowerTier<TowerStats | FenceStats> | null {
  if (path !== 0 && path !== 1) return null;
  const level = pathLevel(tier, path);
  if (level > 0 && pathLevel(tier, 1 - path) > 1) return null;
  return pathsOf(kind)?.[path]?.tiers[level] ?? null;
}

/** How far tower `kind` at tier `tier` reaches (px to a zombie's edge). */
export const towerReach = (kind: number, tier: number): number => towerStats(kind, tier).reach;

/**
 * How towers go about it (ADR-0171): a tower's first shot of a fight waits `readyTicks` (its head swings round); a
 * Pressure Washer's spray throws the dead for `knockTicks`, slowing by `knockDecay` a tick; what burns, poisons or lies
 * underfoot from a tower or a card about you (Killer Bees, Solar Flare, Fire Rounds, a honey puddle, a Jack-o'-Fire's
 * fire) bites every `biteTicks`, its damage a second shared out between the bites. Each upgrade makes a tower tougher:
 * `hpPerTier` of a tower's health as built more (the upgrade mends it by as much).
 * Artillery needs eyes (ADR-0184, `eyes` 1; 0 for a balance experiment): a Mortar's pumpkins and a hive's bees go only for
 * the dead on the ground that one of the party's eyes sees: a gardener's sight, or any tower's, within `sight` px along a
 * clear line (a hedge blocks it). In the open a Mortar sees what it reaches for itself; hidden behind a hedge it needs a
 * tower or a gardener out front to spot for it, and the dead that shoot can put that spotter's eyes out.
 * Only a tower whose numbers say so reaches flyers (`TowerStats.flyers`, ADR-0227: a hive's bees, three tier 2s;
 * `flyers` 1, a balance experiment: every tower does).
 */
export const TOWER = {
  readyTicks: ticks(0.2),
  knockTicks: 6,
  knockDecay: 0.8,
  biteTicks: 10,
  hpPerTier: 0.35,
  sight: 280,
  eyes: 1,
  flyers: 0,
};

/**
 * A build `kind`'s health at tier `tier`: a tower's as built and `TOWER.hpPerTier` of it more an upgrade (on either
 * path), a fence's its tier's (`fenceStats`). A Paragon's (ADR-0181) is `PARAGON.hp` times its third upgrade's, and as
 * many times that again as it hits harder by its degree (`paid`: the gold in it): as tough as all it ate, so the dead
 * never take a party's fortune down in a night.
 */
export function buildHp(kind: number, tier: number, paid = 0): number {
  if (kind === BuildKind.Fence) return fenceStats(tier).hp;
  const hp = buildDef(kind).maxHp;
  if (isParagon(tier))
    return (hp + Math.round(hp * TOWER.hpPerTier) * 3) * PARAGON.hp * paragonPower(paragonDegree(paid));
  return hp + Math.round(hp * TOWER.hpPerTier) * (tierLevel(tier) + crossOf(tier));
}

/** What answers a kind of the dead (`COUNTERS`): `towers` as bought, `upgrades` a tower's path from its tier 2 on. */
export interface Counter {
  readonly towers: readonly TowerKind[];
  readonly upgrades?: readonly { readonly tower: TowerKind; readonly path: 0 | 1 }[];
}

/** The upgrades that reach flyers (ADR-0227): a Pressure Washer's spray, a Solar Lance, a Cluster Pumpkin's bursts. */
const FLYER_UPGRADES: Counter['upgrades'] = [
  { tower: TowerKind.Sprinkler, path: 0 },
  { tower: TowerKind.Sunflower, path: 0 },
  { tower: TowerKind.Mortar, path: 1 },
];

/**
 * The dead that ask for a tower (ADR-0171, ADR-0184, ADR-0227): the dawn before a night that brings one says so
 * (`TONIGHT: BUCKET HEADS` with the icons of the towers that answer it), and nightfall says it again. `towers` answer
 * it as bought, `upgrades` from their path's tier 2 on (a Paragon too: `answers`). A tower's card says the same the
 * other way round: what it is good for (`goodFor`). A hive's bees reach flyers, and so do three tier 2s (`flyers`); a
 * Sprinkler's wet ground brings Diggers up where the towers are; a beam burns through a pail, a pumpkin's blast lands
 * on it, a Killer Bee's sting gets through it (`armour`); a Daylight lights a Wisp, an Ice Sprinkler and Honey Pots
 * see one (`wisps`); only a Mortar outreaches the dead that shoot.
 */
export const COUNTERS: Readonly<Partial<Record<ZombieKind, Counter>>> = {
  [ZombieKind.Crow]: { towers: [TowerKind.Beehive], upgrades: FLYER_UPGRADES },
  [ZombieKind.Spitter]: { towers: [TowerKind.Mortar] },
  [ZombieKind.Digger]: { towers: [TowerKind.Sprinkler, TowerKind.Beehive] },
  [ZombieKind.Deadeye]: { towers: [TowerKind.Mortar] },
  [ZombieKind.Bucket]: {
    towers: [TowerKind.Sunflower, TowerKind.Mortar],
    upgrades: [{ tower: TowerKind.Beehive, path: 0 }],
  },
  // Daylight first: it lights them for every tower.
  [ZombieKind.Wisp]: {
    towers: [],
    upgrades: [
      { tower: TowerKind.Sunflower, path: 1 },
      { tower: TowerKind.Sprinkler, path: 1 },
      { tower: TowerKind.Beehive, path: 1 },
    ],
  },
  [ZombieKind.Moth]: { towers: [TowerKind.Beehive], upgrades: FLYER_UPGRADES },
  // ADR-0226: the weekly deck's variants that ask for a tower (the Grave Robber, the Sapper and the Fence Thief ask
  // for a gun, or Thorns).
  [ZombieKind.Mossback]: { towers: [TowerKind.Mortar, TowerKind.Sunflower] },
  [ZombieKind.Charred]: { towers: [TowerKind.Sprinkler, TowerKind.Mortar] },
  [ZombieKind.Beekeeper]: { towers: [TowerKind.Sunflower] },
  [ZombieKind.Parasol]: { towers: [TowerKind.Sunflower] },
  [ZombieKind.Balloon]: { towers: [TowerKind.Beehive], upgrades: FLYER_UPGRADES },
};

/**
 * Does a tower `tower` (a TowerKind) at tier `tier` answer the dead of kind `kind` (ADR-0227, `COUNTERS`)? Its kind
 * does as bought (`towers`), or it stands at an answering path's tier 2 or past it (`upgrades`), a Paragon on either.
 */
export function answers(kind: number, tower: number, tier: number): boolean {
  const c = COUNTERS[kind as ZombieKind];
  if (c === undefined) return false;
  return (
    c.towers.includes(tower as TowerKind) ||
    (c.upgrades?.some((u) => u.tower === tower && pathLevel(tier, u.path) >= 2) ?? false)
  );
}

/**
 * The kinds night `n` asks about (ADR-0226): its new ones (`newOn`), then those `deck` deals it (a pair once every variant
 * has come: no longer new, still the night's question).
 */
export const tonightKinds = (n: number, deck?: Deck | null): ZombieKind[] => [
  ...new Set([...newOn(n, deck), ...dealtOn(n, deck)]),
];

/** The kind night `n` asks a tower for (the first of its kinds in `COUNTERS`, `tonightKinds`), or null. */
export const askedOn = (n: number, deck?: Deck | null): ZombieKind | null =>
  tonightKinds(n, deck).find((k) => COUNTERS[k] !== undefined) ?? null;

/**
 * The kinds of the dead `tower` is good for as bought (`COUNTERS`' `towers`, the other way round; its upgrades' answers
 * are `answers`'; the weekly deck's variants left out: they come and go, ADR-0226); none for a tower no kind asks for.
 */
export const goodFor = (tower: TowerKind): ZombieKind[] =>
  Object.entries(COUNTERS)
    .filter(([k, c]) => c.towers.includes(tower) && !zombieDef(Number(k)).variant)
    .map(([k]) => Number(k) as ZombieKind);

/**
 * A fence's price (ADR-0098 amended): its shop price for a gardener's first `listPriced` standing, `step` gold more for
 * each one past them (the 41st a gold more, the 100th sixty): a field of fences is still cheap, a carpet of them to
 * the hedges is not. Their own fences count; one broken or sold is one fewer.
 */
export const FENCES = { listPriced: 40, step: 1 } as const;

/** The price of a fence to a gardener who has `owned` of them standing. */
export const fenceCost = (owned: number): number =>
  fenceItem.cost + FENCES.step * Math.max(0, owned - FENCES.listPriced + 1);

/**
 * Selling a build (by day, its owner; anyone once the owner has left): everything it cost (a tower's upgrades too,
 * `Build.paid`) back the day it was built, `refund` of it after.
 */
export const SELL = { refund: 0.75 } as const;

/**
 * No build within `gate` px of a graveyard gate (from a cell's centre, a circle). Spawn points aren't kept clear: a
 * gardener who would stand up on a build stands up beside it (sim `standClear`).
 */
export const BUILD_CLEAR = { gate: 44 } as const;

/**
 * Whether the Greenhouse on `base` stands on cell (cx, cy): its body (`BASE.bodyRadius`, what gardeners and the dead bump
 * into) reaches into the cell, so no build may take it. Nothing keeps a build further off (ADR-0206 amended): on the
 * corner where a farm's lanes cross that is the 4 × 4 round it without its corners, on a cell's centre the 3 × 3.
 */
export function onGreenhouse(base: { x: number; y: number }, cx: number, cy: number): boolean {
  const x = Math.min(Math.max(base.x, cx * TILE_SIZE), (cx + 1) * TILE_SIZE);
  const y = Math.min(Math.max(base.y, cy * TILE_SIZE), (cy + 1) * TILE_SIZE);
  return hypot(base.x - x, base.y - y) < BASE.bodyRadius;
}

// ─── The shop ─────────────────────────────────────────────────────────────────────────────────────────

export type ZombieItemKey = 'fence' | 'repair' | `tower:${TowerDef['key']}`;

export interface ZombieItem {
  readonly id: number;
  readonly key: ZombieItemKey;
  readonly name: string;
  readonly desc: string;
  /** Its price, flat (a fence's grows past the first ones: `fenceCost`). */
  readonly cost: number;
  /** Placed on the map: this build (a tower: `tower`, its TowerKind). */
  readonly build?: BuildKind;
  readonly tower?: TowerKind;
}

/** Everything the day's shop sells, by id (the `zombie` message's `item`): a fence, the towers, a repair. */
export const ZOMBIE_SHOP: readonly ZombieItem[] = [
  {
    key: 'fence',
    name: 'Fence',
    desc: 'Blocks the dead, not you',
    cost: 15,
    build: BuildKind.Fence,
  } as Omit<ZombieItem, 'id'>,
  ...TOWERS.map(
    (t): Omit<ZombieItem, 'id'> => ({
      key: `tower:${t.key}`,
      name: t.name,
      desc: t.desc,
      cost: t.cost,
      build: BuildKind.Tower,
      tower: t.kind,
    }),
  ),
  { key: 'repair', name: 'Repair', desc: 'Greenhouse +300 health', cost: 60 } as Omit<ZombieItem, 'id'>,
].map((item, id) => ({ ...item, id }));

export const zombieItem = (id: number): ZombieItem | undefined => ZOMBIE_SHOP[id];

/** The shop item of tower `kind` (a TowerKind). */
export const towerItem = (kind: number): ZombieItem | undefined => ZOMBIE_SHOP.find((i) => i.tower === kind);
export const fenceItem = ZOMBIE_SHOP.find((i) => i.key === 'fence') as ZombieItem;

/** Repair: the Greenhouse regains `repairHp` a buy. */
export const ZOMBIE_GEAR = { repairHp: 300 } as const;

// ─── Cards ────────────────────────────────────────────────────────────────────────────────────────────

/**
 * What each dawn offers a gardener (ADR-0098): `offered` cards to take one of — weapons after odd nights, stats after even
 * ones (a run starts with the Pistol). A weapon card puts that gun in your hands for the run, in place of the one you
 * have; a stat card adds a level of it (levels stack, up to the stat's `max`). An epic stat (`ZombiePerk.epic`) comes
 * `epicWeight` times as often as a plain one, and once a run.
 */
export const CARDS = { offered: 3, epicWeight: 0.15 } as const;

/** What the cards on offer are: none, weapons, stats (`ZombiePerk`s). */
export const CardOffer = { None: 0, Weapons: 1, Stats: 2 } as const;
export type CardOffer = (typeof CardOffer)[keyof typeof CardOffer];

/**
 * The guns a weapon card can be, by how often (`weight`) among their kind: the plain guns, and the crowd clearers (`aoe`:
 * splash, flame, fire and puddles), which come by night (`aoeChance`). Never the Laser or the Hive Launcher (ADR-0171: a
 * Sunflower's and a Beehive's lookalikes).
 */
export const CARD_WEAPONS: readonly { readonly weapon: WeaponDef; readonly weight: number; readonly aoe?: true }[] = [
  { weapon: THORNFIRE, weight: 10 },
  { weapon: SHOTGUN, weight: 10 },
  { weapon: SHURIKEN, weight: 10 },
  { weapon: POISON_DART, weight: 10 },
  { weapon: FIREFLY, weight: 10 },
  { weapon: LONGSTEM, weight: 10 },
  { weapon: FROSTBITE, weight: 10 },
  { weapon: PRUNER, weight: 6 },
  { weapon: BERSERKER, weight: 4 },
  { weapon: BUZZSAW, weight: 5 },
  { weapon: WHACKER, weight: 5 },
  { weapon: DUBSTEP, weight: 4 },
  { weapon: SHIELD, weight: 4 },
  { weapon: CORN_RAIL, weight: 1.5 },
  { weapon: WEED_BURNER, weight: 1, aoe: true },
  { weapon: SLUDGE, weight: 1, aoe: true },
  { weapon: FIREWORK, weight: 1, aoe: true },
  { weapon: ROOT_ROCKET, weight: 1, aoe: true },
];

/**
 * Does `weapon` never run out of rounds: an endless gun, or any gun on a Zombie Nights map (`zombies`, ADR-0098
 * amended)? A reload took a quarter to half of a gun's firing time, and stopping to do it in the middle of a night was
 * no fun; a Shield's blocks cost no charge there either.
 */
export const isEndless = (weapon: WeaponDef, zombies: boolean): boolean => weapon.endless === true || zombies;

/**
 * A gun's job in Zombie Nights (ADR-0168): the question it answers, said on the dawn's card (`job`); its damage on the
 * dead in a gardener's hands (`damage` times its own), and `times` that again on the dead it is the answer to (`vs`).
 * The Homing Gun, tuned for the war, where it never misses, did half a Shotgun's there; the Freeze Gun only slowed. One
 * gun a whole run (the harness's `plaza` defender alone, `--gun`): a job gun lasts about as long as the crowd clearers.
 */
export interface GunJob {
  readonly job: string;
  readonly damage: number;
  readonly vs: readonly ZombieKind[];
  readonly times: number;
}

export const GUN_JOBS: Readonly<Partial<Record<number, GunJob>>> = {
  [FIREFLY.id]: { job: 'VS FLYERS', damage: 2, vs: [ZombieKind.Crow, ZombieKind.Moth, ZombieKind.Digger], times: 2 },
  [FROSTBITE.id]: { job: 'VS HEARSES', damage: 2.2, vs: [ZombieKind.Hearse, ZombieKind.Runner], times: 2 },
  [LONGSTEM.id]: {
    job: 'VS SPITTERS',
    damage: 2,
    vs: [ZombieKind.Spitter, ZombieKind.Deadeye, ZombieKind.Lobber],
    times: 2,
  },
  [SHIELD.id]: { job: 'BLOCKS PUMPKINS', damage: 1, vs: [], times: 1 },
  [SHOTGUN.id]: { job: 'VS BRUTES', damage: 1.5, vs: [ZombieKind.Brute], times: 1.5 },
  [WEED_BURNER.id]: { job: 'VS CROWDS', damage: 1, vs: [], times: 1 },
  [SLUDGE.id]: { job: 'VS CROWDS', damage: 1, vs: [], times: 1 },
  [POISON_DART.id]: { job: 'VS BOSSES', damage: 1, vs: [], times: 1 },
  // Its shot goes through a pail (ADR-0171: `ZombieDef.armoured`).
  [CORN_RAIL.id]: { job: 'VS BUCKET HEADS', damage: 1, vs: [ZombieKind.Bucket], times: 1 },
};

/** What a gardener's hit with content weapon `weapon` on zombie kind `kind` does, as a multiple (`GUN_JOBS`, `NIGHTS.gunScale`). */
export const gunJobScale = (weapon: number, kind: number): number => {
  const j = GUN_JOBS[weapon];
  const gun = WEAPONS[weapon];
  const lost = gun !== undefined && !gun.turret && !gun.endless;
  return (j ? j.damage * (j.vs.includes(kind as ZombieKind) ? j.times : 1) : 1) * (lost ? NIGHTS.gunScale : 1);
};

/** A weapon offer's last card (ADR-0098 amended): taken, you keep the gun in your hands. */
export const KEEP_CARD = 0xfe;

/**
 * The card of offer `cards` nightfall takes when none was picked (and the AUTO tag marks): the Keep when one is offered,
 * so your gun stays, else the first.
 */
export const autoCard = (cards: readonly number[]): number => Math.max(0, cards.indexOf(KEEP_CARD));

/**
 * How likely each gun card of the dawn after night `n` is a crowd clearer (`CARD_WEAPONS`' `aoe`): rare in the first
 * nights, about one card in four from night 9 (between the anchors, a straight line). A run has been offered one by night
 * 10 about four times in five, about three by night 15 and four by night 20 (`zombies.test.ts` counts them).
 */
export const AOE_CHANCE: readonly (readonly [night: number, chance: number])[] = [
  [1, 0.02],
  [3, 0.06],
  [5, 0.12],
  [7, 0.22],
  [9, 0.27],
];

/** `AOE_CHANCE` on night `n`. */
export function aoeChance(n: number): number {
  let [n0, c0] = AOE_CHANCE[0] as readonly [number, number];
  if (n <= n0) return c0;
  for (const [n1, c1] of AOE_CHANCE) {
    if (n <= n1) return c0 + ((c1 - c0) * (n - n0)) / Math.max(1, n1 - n0);
    [n0, c0] = [n1, c1];
  }
  return c0;
}

/**
 * The weapon offer of the dawn after night `day` to the gardener `id` holding `gun`, drawn by the run's `seed`:
 * `CARDS.offered - 1` guns — each a crowd clearer with `aoeChance(day)` (else a plain gun), picked among its kind by
 * weight, never the gun in your hands nor one twice — and a Keep (`KEEP_CARD`). The first dawn has no Keep (`keep`; nor
 * a later night's first day): the starter Pistol is not worth keeping, so it is `CARDS.offered` guns.
 */
export function weaponCards(seed: number, day: number, id: number, gun: number, keep = day > 1): number[] {
  const pool = CARD_WEAPONS.filter((w) => w.weapon.id !== gun);
  const out: number[] = [];
  const guns = keep ? CARDS.offered - 1 : CARDS.offered;
  for (let k = 0; k < guns && pool.length > 0; k++) {
    const aoe = hash32(seed, day, id, k, 0x616f65) / 0x100000000 < aoeChance(day);
    const kind = pool.filter((w) => (w.aoe === true) === aoe);
    const from = kind.length > 0 ? kind : pool;
    let r = (hash32(seed, day, id, k) / 0x100000000) * from.reduce((s, w) => s + w.weight, 0);
    let i = 0;
    while (i < from.length - 1 && r >= (from[i] as { weight: number }).weight)
      r -= (from[i++] as { weight: number }).weight;
    const pick = from[i] as (typeof CARD_WEAPONS)[number];
    out.push(pick.weapon.id);
    pool.splice(pool.indexOf(pick), 1);
  }
  if (keep) out.push(KEEP_CARD);
  return out;
}

/** A stat a card raises by a level (`PERK` says by how much). */
export interface ZombiePerk {
  readonly key:
    | 'damage'
    | 'fireRate'
    | 'speed'
    | 'vampire'
    | 'greed'
    | 'splash'
    | 'frost'
    | 'fire'
    | 'pierce'
    | 'chain'
    | 'execute'
    | 'bigGame'
    | 'midas'
    | 'crit'
    | 'armour'
    | 'leech';
  readonly name: string;
  /** What a level gives in a few words, for its card. */
  readonly desc: string;
  /** The most levels you can stack: past it its card no longer comes. */
  readonly max: number;
  /** An epic (ADR-0171): rare (`CARDS.epicWeight`), once a run (`max` 1), strong. */
  readonly epic?: true;
}

/**
 * The stats (`PerkKind` order: also the order of `SelfState.perks`), each about you (ADR-0171): your gun's damage, rate
 * and hits' effects, your pace, your gold, your health.
 */
export const PERKS: readonly ZombiePerk[] = [
  { key: 'damage', name: 'Damage', desc: '+20% gun damage', max: 10 },
  { key: 'fireRate', name: 'Fire rate', desc: 'Fire 15% faster', max: 8 },
  { key: 'speed', name: 'Swift', desc: 'Move 12% faster', max: 6 },
  { key: 'vampire', name: 'Harvest', desc: 'Heal 4 a kill', max: 6 },
  { key: 'greed', name: 'Greed', desc: '+20% kill gold', max: 6 },
  { key: 'splash', name: 'Splash Rounds', desc: 'Hits splash nearby', max: 3 },
  { key: 'frost', name: 'Frost Rounds', desc: 'Hits slow the dead', max: 3 },
  { key: 'fire', name: 'Fire Rounds', desc: 'Hits set fire', max: 3 },
  { key: 'pierce', name: 'Piercing Rounds', desc: 'Shots pierce armor', max: 3 },
  {
    key: 'chain',
    name: 'Chain Reaction',
    desc: 'Kills explode',
    max: 1,
    epic: true,
  },
  { key: 'execute', name: 'Executioner', desc: 'Executes under 20%', max: 1, epic: true },
  { key: 'bigGame', name: 'Big Game Hunter', desc: '2× vs bosses, Brutes', max: 1, epic: true },
  { key: 'midas', name: 'Midas Touch', desc: 'Kills pay double', max: 1, epic: true },
  // The war's items once (ADR-0141), dealt at Zombie Nights' dawns since ADR-0171.
  { key: 'crit', name: 'Lucky Clover', desc: '10% crit chance', max: 5 },
  { key: 'armour', name: 'Bark Armor', desc: '+15 health, 5% armor', max: 5 },
  { key: 'leech', name: 'Leech Seeds', desc: 'Heal 4% of damage', max: 5 },
];

/** The levels of every stat (`PERKS` order) named in `crit:2,splash:3` (a dev knob's: their keys), the rest none. */
export const perksNamed = (levels: string): number[] => {
  const named = new Map(levels.split(',').map((kv) => kv.split(':').map((s) => s.trim()) as [string, string]));
  return PERKS.map((p) => Math.max(0, Math.floor(Number(named.get(p.key))) || 0));
};

export { PerkKind } from './perk-kind';

/**
 * What a level of each stat does: Damage is `DAMAGE_BOOST_PER_LEVEL` a level; `fireRate` the
 * share faster; `speed` the share faster; `vampire` health a kill; `greed` the share more gold.
 */
export const PERK = {
  fireRate: 0.15,
  speed: 0.12,
  vampire: 4,
  greed: 0.2,
  /**
   * Zombie Nights' cards about you (ADR-0171), each on your own hits and kills (never your towers'):
   * - Splash Rounds: a hit of yours bursts for `splashShare` of its damage on the other dead within `splashRadius` +
   *   `splashPerLevel` a level px of where it hit.
   * - Frost Rounds: your hits soak the dead (`Frozen`) for `frostTicks` a level.
   * - Fire Rounds: your hits set the dead on fire, `fireDamage` a second a level for `fireTicks`.
   * - Piercing Rounds: your shots go through `pierce` more of the dead a level, and through armour (`ZombieDef.armoured`).
   * - Chain Reaction: the dead you kill burst for `chainShare` of their max health (at least `chainMin`) on the dead within
   *   `chainRadius` px (never a mega boss's part); a burst sets off no card, so none chains on.
   * - Executioner: your hit that hurts finishes one of the dead left under `execute` of its max health (not what burns
   *   or lies underfoot; never a boss or a mega boss's part).
   * - Big Game Hunter: `bigGame` times your damage on bosses (the mega bosses' parts too), Brutes and hearses.
   * - Midas Touch: the dead you kill pay `midas` times their bounty.
   */
  splashShare: 0.35,
  splashRadius: 12,
  splashPerLevel: 6,
  frostTicks: ticks(0.4),
  fireDamage: 3,
  fireTicks: ticks(2),
  pierce: 1,
  chainShare: 0.3,
  chainMin: 20,
  chainRadius: 36,
  execute: 0.2,
  bigGame: 2,
  midas: 2,
  /**
   * A Lucky Clover's chance a hit of yours crits (`critDamage` times as hard); a Bark Armour's max health (`armourHp`)
   * and share of every blow it takes off (`armourCut`); a Leech Seed's share of the damage you deal back as health.
   */
  crit: 0.1,
  critDamage: 2,
  armourHp: 15,
  armourCut: 0.05,
  leech: 0.04,
} as const;

/** The cards dawn `day` (after night `day`) brings: weapons after odd nights, stats after even ones. */
export const offerAfter = (day: number): CardOffer => (day % 2 === 1 ? CardOffer.Weapons : CardOffer.Stats);
